/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import * as exifr from "exifr";
import { db, storage } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import LifeformPicker from "./LifeformPicker";
import { speciesNames } from "../utils/speciesNames";
import {
  generaByFamily,
  familiesByOrder,
  ordersByClass,
  classesByPhylum,
  phylaByKingdom,
} from "../utils/taxonomy";
import LocationPickerModal from "./LocationPickerModal";
import PlantSpeciesDescription from "./PlantSpeciesDescription";
import EcologicalRelationships from "./EcologicalRelationships";
import { useSearchParams } from "react-router-dom";
import useAdminClaim from "../hooks/useAdminClaim";
import "../styles/SpeciesPhotoModalProfessional.css";

import geminiLogo from "../assets/gemini.png";
import inatLogo from "../assets/inat.png";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  deleteDoc,
  setDoc,
  where,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { nanoid } from "nanoid";

// Gemini suggestions must identify a species or a confirmed genus. The callable
// may return a rank; older responses without one are accepted at genus level
// only when that genus is present in our taxonomy or species-name catalog.
const KNOWN_HIGHER_TAXA = new Set(
  [familiesByOrder, ordersByClass, classesByPhylum, phylaByKingdom]
    .flatMap((groups) => [
      ...Object.keys(groups),
      ...Object.values(groups).flat(),
    ])
    .map((name) => String(name).toLowerCase()),
);
const KNOWN_GENERA = new Set(
  [
    ...Object.values(generaByFamily).flat(),
    ...speciesNames.map(
      (species) => String(species.scientific || "").split(/\s+/)[0],
    ),
  ].map((name) => String(name).toLowerCase()),
);

function reviewGeminiSuggestion(suggestion) {
  const name = String(
    suggestion?.scientificName || suggestion?.suggestedTaxonName || "",
  )
    .trim()
    .replace(/\s+/g, " ");
  const rank = String(
    suggestion?.rank ||
      suggestion?.suggestedTaxonRank ||
      suggestion?.taxonRank ||
      suggestion?.taxonomicRank ||
      "",
  )
    .trim()
    .toLowerCase();
  const parts = name.split(" ");
  const genus = parts[0] || "";
  const higherRank =
    KNOWN_HIGHER_TAXA.has(name.toLowerCase()) ||
    /(?:aceae|ales|idae|inae|oidea|iformes|phyta|mycota|opsida)$/i.test(name);
  const genusValid =
    /^[A-Z][\p{L}-]+$/u.test(genus) &&
    !KNOWN_HIGHER_TAXA.has(genus.toLowerCase()) &&
    !/(?:aceae|ales|idae|inae|oidea|iformes|phyta|mycota|opsida)$/i.test(genus);
  const speciesValid =
    parts.length === 2 && genusValid && /^[a-z][\p{L}-]+$/u.test(parts[1]);
  const genusOnly =
    parts.length === 1 &&
    genusValid &&
    (rank === "genus" || (!rank && KNOWN_GENERA.has(genus.toLowerCase())));

  if (!higherRank && (rank === "species" || !rank) && speciesValid) {
    return { accepted: true, name, rank: "species" };
  }
  if (!higherRank && genusOnly) {
    return { accepted: true, name, rank: "genus" };
  }
  const normalizedName = name.toLowerCase();
  const inferredRank = Object.keys(familiesByOrder).some(
    (order) => order.toLowerCase() === normalizedName,
  )
    ? "order"
    : Object.keys(generaByFamily).some(
          (family) => family.toLowerCase() === normalizedName,
        ) || /(?:aceae|idae)$/i.test(name)
      ? "family"
      : "an unconfirmed taxon";
  const identifiedRank =
    rank && !["genus", "species"].includes(rank) ? rank : inferredRank;
  return { accepted: false, name, rank: identifiedRank };
}

function defaultFirstSort(a, b) {
  const ad = a?.isDefault ? 1 : 0;
  const bd = b?.isDefault ? 1 : 0;
  if (bd !== ad) return bd - ad;
  return 0;
}

function normalizeSpeciesName(name) {
  return String(name || "")
    .trim()
    .toLowerCase();
}

function ensureSingleDefault(forms) {
  const arr = Array.isArray(forms) ? [...forms] : [];
  if (arr.length === 0) return arr;

  const hasDefault = arr.some((f) => f?.isDefault);
  if (!hasDefault) {
    arr[0] = { ...arr[0], isDefault: true };
  } else {
    let found = false;
    for (let i = 0; i < arr.length; i++) {
      if (arr[i]?.isDefault) {
        if (!found) {
          found = true;
        } else {
          arr[i] = { ...arr[i], isDefault: false };
        }
      }
    }
  }

  return arr.sort(defaultFirstSort);
}

function safeFileName(name) {
  return (name || "image").replace(/[^\w.\- ()]/g, "_");
}

const DAYS = [
  "NA",
  ...Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")),
];

const MONTHS = [
  { value: "NA", label: "NA" },
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const YEARS = [
  "NA",
  ...Array.from({ length: 80 }, (_, i) => String(new Date().getFullYear() - i)),
];

function normalizeDate(value) {
  if (!value) return { year: "NA", month: "NA", day: "NA" };

  if (typeof value === "object") {
    return {
      year: value.year || "NA",
      month: value.month || "NA",
      day: value.day || "NA",
    };
  }

  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return { year: "NA", month: "NA", day: "NA" };

    const parts = raw.split("-");
    if (parts.length === 1) {
      return { year: parts[0] || "NA", month: "NA", day: "NA" };
    }
    if (parts.length === 2) {
      return { year: parts[0] || "NA", month: parts[1] || "NA", day: "NA" };
    }
    if (parts.length >= 3) {
      return {
        year: parts[0] || "NA",
        month: parts[1] || "NA",
        day: parts[2] || "NA",
      };
    }
  }

  return { year: "NA", month: "NA", day: "NA" };
}

function formatDateForDisplay(dateVal) {
  const d = normalizeDate(dateVal);

  if (!d.year || d.year === "NA") return "Unknown";

  if (!d.month || d.month === "NA") {
    return d.year;
  }

  const monthName =
    MONTHS.find((month) => month.value === d.month)?.label || d.month;

  if (!d.day || d.day === "NA") {
    return `${monthName} ${d.year}`;
  }

  const day = String(Number(d.day)); // removes leading zero

  return `${day} ${monthName} ${d.year}`;
}

async function extractDateFromExif(file) {
  try {
    const exif = await exifr.parse(file, ["DateTimeOriginal", "CreateDate"]);
    const date = exif?.DateTimeOriginal || exif?.CreateDate;

    if (!date) return { day: "NA", month: "NA", year: "NA" };

    return {
      day: String(date.getDate()).padStart(2, "0"),
      month: String(date.getMonth() + 1).padStart(2, "0"),
      year: String(date.getFullYear()),
    };
  } catch {
    return { day: "NA", month: "NA", year: "NA" };
  }
}

async function makeNewAddEntry(file) {
  const date = await extractDateFromExif(file);

  return {
    id: `${file.name}_${file.size}_${file.lastModified}_${Math.random().toString(16).slice(2)}`,
    file,
    previewUrl: URL.createObjectURL(file),
    label: "",
    lifeformKingdom: "",
    isDefault: false,
    date,
    location: "",
    locationName: "",
    locationLat: null,
    locationLng: null,
    locationCountry: "",
    locationAdminArea: "",
    locationCity: "",
    locationPlaceId: "",
    variant: "",
    notes: "",
    collections: [],
    showAdditionalInfo: false,
    showVariantField: false,
    showCollectionsField: false,
    showNotesField: false,
    uploadStatus: "pending",
  };
}

async function bumpProfileCacheMarkers(uid, sections = {}) {
  if (!uid) return;

  const now = Date.now();
  const sectionUpdatedAt = {};

  if (sections.taxonomy) sectionUpdatedAt.taxonomy = now;
  if (sections.collections) sectionUpdatedAt.collections = now;
  if (sections.quiz) sectionUpdatedAt.quiz = now;
  if (sections.profile) sectionUpdatedAt.profile = now;

  if (Object.keys(sectionUpdatedAt).length === 0) return;

  await setDoc(doc(db, "profile", uid), { sectionUpdatedAt }, { merge: true });
}

function hasMappedLocation(form) {
  return form?.locationLat != null && form?.locationLng != null;
}

function displayLocation(form) {
  return form?.locationName || form?.location || "";
}

function formToLocationData(form) {
  if (!form?.locationLat || !form?.locationLng) return null;

  return {
    name: form.locationName || form.location || "Selected location",
    lat: form.locationLat,
    lng: form.locationLng,
    country: form.locationCountry || "",
    adminArea: form.locationAdminArea || "",
    city: form.locationCity || "",
    placeId: form.locationPlaceId || "",
  };
}

function observationAuditSnapshot(speciesData = {}, formData = {}) {
  return {
    speciesId: String(speciesData.id || ""),
    scientificName: String(speciesData.name || ""),
    commonName: String(speciesData.common || ""),
    formId: String(formData.id || ""),
    formURL: String(formData.url || ""),
    label: String(formData.label || ""),
    date: {
      day: String(formData?.date?.day || "NA"),
      month: String(formData?.date?.month || "NA"),
      year: String(formData?.date?.year || "NA"),
    },
    location: {
      name: String(formData.locationName || formData.location || ""),
      lat:
        typeof formData.locationLat === "number" ? formData.locationLat : null,
      lng:
        typeof formData.locationLng === "number" ? formData.locationLng : null,
      country: String(formData.locationCountry || ""),
      adminArea: String(formData.locationAdminArea || ""),
      city: String(formData.locationCity || ""),
      placeId: String(formData.locationPlaceId || ""),
    },
    variant: String(formData.variant || ""),
    notes: String(formData.notes || ""),
    collections: Array.isArray(formData.collections)
      ? formData.collections.map((collectionName) => String(collectionName))
      : [],
    isDefault: !!formData.isDefault,
  };
}

export default function SpeciesPhotoModal({
  selected,
  setSelected,
  photoIndex,
  setPhotoIndex,
  dataUID,
  currentUID,
  uid,
  kingdomName,
  isOwnCollection,
  isAdminCollectionMode = false,
  setSpeciesList,
  onRefresh,
  preloadedFriendSpeciesByName = null,
  preloadedFriendProfiles = null,
  preloadedFriendSpeciesLoading = false,
}) {
  const [searchParams] = useSearchParams();
  const { isAdmin, adminLoading } = useAdminClaim();

  const adminModeRequested = searchParams.get("adminMode") === "true";

  const [loadedFriendIcons, setLoadedFriendIcons] = useState({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [shareCardOpen, setShareCardOpen] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editForms, setEditForms] = useState([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editDirty, setEditDirty] = useState(false);

  const [isAddingPhoto, setIsAddingPhoto] = useState(false);
  const [addEntries, setAddEntries] = useState([]);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState(null);
  const [fileLimitError, setFileLimitError] = useState(null);

  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState(null);
  const [renameCommon, setRenameCommon] = useState("");
  const [renameSelectedFormIds, setRenameSelectedFormIds] = useState([]);

  const [renameSuggestions, setRenameSuggestions] = useState([]);
  const [renameActiveIndex, setRenameActiveIndex] = useState(0);
  const [renameActiveField, setRenameActiveField] = useState(null);
  const [renameGeminiStatus, setRenameGeminiStatus] = useState("idle");
  const [renameGeminiDialog, setRenameGeminiDialog] = useState(null);

  const [confirmationDialog, setConfirmationDialog] = useState(null);
  const [collectionNameDialog, setCollectionNameDialog] = useState(null);

  const [adminDeleteTarget, setAdminDeleteTarget] = useState(null);
  const [adminDeleteReason, setAdminDeleteReason] = useState("");
  const [adminDeleteError, setAdminDeleteError] = useState("");
  const [adminDeleteSaving, setAdminDeleteSaving] = useState(false);

  const [viewMode, setViewMode] = useState("mine"); // "mine" | "friends"
  const [friendForms, setFriendForms] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const isViewingOwnPhotos = viewMode === "mine";

  const isAdminManagingCollection =
    !adminLoading &&
    isAdmin &&
    !!dataUID &&
    dataUID !== currentUID &&
    (isAdminCollectionMode || adminModeRequested);

  const canEditCollectionMetadata =
    (isOwnCollection || isAdminManagingCollection) && isViewingOwnPhotos;

  const canReclassifyCollectionObservation =
    (isOwnCollection || isAdminManagingCollection) && isViewingOwnPhotos;

  const canDeleteCollectionObservation =
    isAdminManagingCollection && isViewingOwnPhotos;

  const [friendProfiles, setFriendProfiles] = useState({});
  const [selectedFriendUID, setSelectedFriendUID] = useState(null);
  const [blockedUserIds, setBlockedUserIds] = useState([]);
  const [blockedByUserIds, setBlockedByUserIds] = useState([]);
  const [blockRelationshipsLoading, setBlockRelationshipsLoading] =
    useState(true);
  const [collectionsList, setCollectionsList] = useState([]);
  const [locationModal, setLocationModal] = useState(null);
  const [collectionsPopoverOpen, setCollectionsPopoverOpen] = useState(false);
  const functions = getFunctions(undefined, "southamerica-east1");

  const isPlantSpecies = String(kingdomName || "").toLowerCase() === "plantae";

  function updateParentSpeciesList(updater) {
    if (typeof setSpeciesList === "function") {
      setSpeciesList(updater);
    }

    if (typeof onRefresh === "function") {
      window.setTimeout(onRefresh, 0);
    }
  }

  const speciesModalHistoryPushedRef = useRef(false);
  const activeSpeciesModalRef = useRef(false);

  const activeSpeciesModal = useMemo(
    () =>
      selected ||
      isFullscreen ||
      shareCardOpen ||
      isEditing ||
      isAddingPhoto ||
      isRenaming ||
      adminDeleteTarget ||
      locationModal ||
      collectionsPopoverOpen,
    [
      selected,
      isFullscreen,
      shareCardOpen,
      isEditing,
      isAddingPhoto,
      isRenaming,
      adminDeleteTarget,
      locationModal,
      collectionsPopoverOpen,
    ],
  );

  useEffect(() => {
    activeSpeciesModalRef.current = activeSpeciesModal;

    if (activeSpeciesModal && !speciesModalHistoryPushedRef.current) {
      window.history.pushState(
        { ...(window.history.state || {}), speciesModal: true },
        "",
        window.location.href,
      );

      speciesModalHistoryPushedRef.current = true;
    }

    if (!activeSpeciesModal) {
      speciesModalHistoryPushedRef.current = false;
    }
  }, [activeSpeciesModal]);

  useEffect(() => {
    function handleSpeciesModalBack() {
      if (!activeSpeciesModalRef.current) return;

      setSelected(null);
      setIsFullscreen(false);
      setShareCardOpen(false);
      setPhotoIndex(0);
      setIsEditing(false);
      setEditForms([]);
      setEditSaving(false);
      setEditError(null);
      setIsAddingPhoto(false);
      setAddSaving(false);
      setAddError(null);
      setFileLimitError(null);
      setIsRenaming(false);
      setRenameValue("");
      setRenameCommon("");
      setRenameSaving(false);
      setRenameError(null);
      setRenameSuggestions([]);
      setRenameSelectedFormIds([]);
      setRenameGeminiStatus("idle");
      setRenameGeminiDialog(null);
      setAdminDeleteTarget(null);
      setAdminDeleteReason("");
      setAdminDeleteError("");
      setAdminDeleteSaving(false);
      setLocationModal(null);
      setCollectionsPopoverOpen(false);

      activeSpeciesModalRef.current = false;
      speciesModalHistoryPushedRef.current = false;
    }

    window.addEventListener("popstate", handleSpeciesModalBack);

    return () => {
      window.removeEventListener("popstate", handleSpeciesModalBack);
    };
  }, [setPhotoIndex, setSelected]);

  const blockedRelationshipUserIdSet = useMemo(
    () => new Set([...blockedUserIds, ...blockedByUserIds]),
    [blockedUserIds, blockedByUserIds],
  );

  const selectedOwnerInteractionBlocked =
    !isAdminManagingCollection &&
    !!dataUID &&
    dataUID !== currentUID &&
    blockedRelationshipUserIdSet.has(dataUID);

  useEffect(() => {
    if (isAdminManagingCollection) {
      setBlockedUserIds([]);
      setBlockedByUserIds([]);
      setBlockRelationshipsLoading(false);
      return undefined;
    }

    if (!currentUID) {
      setBlockedUserIds([]);
      setBlockedByUserIds([]);
      setBlockRelationshipsLoading(false);
      return undefined;
    }

    let outgoingBlocksLoaded = false;
    let incomingBlocksLoaded = false;

    function finishBlockRelationshipLoading() {
      if (outgoingBlocksLoaded && incomingBlocksLoaded) {
        setBlockRelationshipsLoading(false);
      }
    }

    setBlockRelationshipsLoading(true);

    const outgoingBlocksQuery = query(
      collection(db, "userBlocks"),
      where("blockerUid", "==", currentUID),
    );

    const incomingBlocksQuery = query(
      collection(db, "userBlocks"),
      where("blockedUid", "==", currentUID),
    );

    const unsubscribeOutgoingBlocks = onSnapshot(
      outgoingBlocksQuery,
      (snapshot) => {
        setBlockedUserIds(
          snapshot.docs
            .map((blockDocument) => blockDocument.data().blockedUid)
            .filter(Boolean),
        );

        outgoingBlocksLoaded = true;
        finishBlockRelationshipLoading();
      },
      (error) => {
        console.error("Failed to load blocked photo users:", error);
        outgoingBlocksLoaded = true;
        finishBlockRelationshipLoading();
      },
    );

    const unsubscribeIncomingBlocks = onSnapshot(
      incomingBlocksQuery,
      (snapshot) => {
        setBlockedByUserIds(
          snapshot.docs
            .map((blockDocument) => blockDocument.data().blockerUid)
            .filter(Boolean),
        );

        incomingBlocksLoaded = true;
        finishBlockRelationshipLoading();
      },
      (error) => {
        console.error("Failed to load incoming photo blocks:", error);
        incomingBlocksLoaded = true;
        finishBlockRelationshipLoading();
      },
    );

    return () => {
      unsubscribeOutgoingBlocks();
      unsubscribeIncomingBlocks();
    };
  }, [currentUID, isAdminManagingCollection]);

  const currentForms = useMemo(() => {
    if (selectedOwnerInteractionBlocked) return [];

    let forms = selected?.forms || [];

    if (viewMode === "friends") {
      forms = selectedFriendUID
        ? friendForms.filter((f) => f.ownerUID === selectedFriendUID)
        : [];
    }

    return Array.isArray(forms) ? forms.slice().sort(defaultFirstSort) : [];
  }, [
    selected,
    friendForms,
    viewMode,
    selectedFriendUID,
    selectedOwnerInteractionBlocked,
  ]);

  useEffect(() => {
    setPhotoIndex((i) => {
      if (currentForms.length === 0) return 0;
      return Math.min(Math.max(i, 0), currentForms.length - 1);
    });
  }, [currentForms.length, setPhotoIndex]);

  const currentForm = currentForms[photoIndex] || null;
  const currentPhotoSrc = currentForm?.url;

  // Size the desktop record from the actual photograph, not the surrounding
  // ecological-relationship cards or other components.
  const [photoDimensions, setPhotoDimensions] = useState(null);
  const [viewportSize, setViewportSize] = useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768,
  }));

  useEffect(() => {
    const updateViewport = () =>
      setViewportSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  const desktopPhotoWidth = useMemo(() => {
    if (!photoDimensions || photoDimensions.src !== currentPhotoSrc) return 560;
    const { width, height } = photoDimensions;
    const maximumWidth = Math.max(200, Math.min(viewportSize.width - 96, 1100));
    const maximumHeight = Math.max(150, viewportSize.height * 0.62);
    const scale = Math.min(1, maximumWidth / width, maximumHeight / height);
    return Math.max(160, Math.round(width * scale));
  }, [photoDimensions, currentPhotoSrc, viewportSize]);

  const friendOwners = useMemo(
    () => [
      ...new Map(
        friendForms
          .filter((form) => form?.ownerUID)
          .map((form) => [form.ownerUID, form]),
      ).values(),
    ],
    [friendForms],
  );

  useEffect(() => {
    if (!selected?.forms || selected.forms.length === 0) return;

    selected.forms.forEach((form) => {
      if (!form?.url) return;

      const img = new Image();
      img.src = form.url;
    });
  }, [selected]);

  useEffect(() => {
    if (!friendProfiles) return;

    Object.entries(friendProfiles).forEach(([uid, profile]) => {
      if (!profile?.photoURL) return;

      const img = new Image();
      img.src = profile.photoURL;

      img.onload = () => {
        setLoadedFriendIcons((prev) => ({
          ...prev,
          [uid]: true,
        }));
      };
    });
  }, [friendProfiles]);

  useEffect(() => {
    setViewMode("mine");
    setSelectedFriendUID(null);
    setFriendForms([]);
    setLoadingFriends(false);
  }, [selected?.id, selected?.name]);

  useEffect(() => {
    if (
      !selected?.name ||
      !currentUID ||
      !isOwnCollection ||
      selectedOwnerInteractionBlocked
    ) {
      setFriendForms([]);
      setLoadingFriends(false);
      return;
    }

    if (preloadedFriendSpeciesByName !== null) {
      const preloadedEntry =
        preloadedFriendSpeciesByName[normalizeSpeciesName(selected.name)];

      setFriendForms(
        Array.isArray(preloadedEntry?.forms)
          ? preloadedEntry.forms.slice().sort(defaultFirstSort)
          : [],
      );
      setFriendProfiles(preloadedFriendProfiles || {});
      setLoadingFriends(preloadedFriendSpeciesLoading);
      return;
    }

    if (blockRelationshipsLoading) return;

    let cancelled = false;

    async function loadFriendPhotos() {
      setLoadingFriends(true);
      try {
        const friendsSnap = await getDocs(
          collection(db, "users", currentUID, "friends"),
        );

        const friendUIDs = friendsSnap.docs
          .map((d) => d.id)
          .filter((friendUid) => !blockedRelationshipUserIdSet.has(friendUid));

        const [ownProfileSnap, friendRows] = await Promise.all([
          getDoc(doc(db, "profile", currentUID)),
          Promise.all(
            friendUIDs.map(async (friendUID) => {
              const [profileSnap, speciesSnap] = await Promise.all([
                getDoc(doc(db, "profile", friendUID)),
                getDocs(
                  query(
                    collection(db, "users", friendUID, "species"),
                    where("name", "==", selected.name),
                  ),
                ),
              ]);

              return {
                friendUID,
                profile: profileSnap.exists() ? profileSnap.data() : {},
                species: speciesSnap.empty ? null : speciesSnap.docs[0].data(),
              };
            }),
          ),
        ]);

        const profileMap = ownProfileSnap.exists()
          ? { [currentUID]: ownProfileSnap.data() }
          : {};
        const allForms = [];

        friendRows.forEach(({ friendUID, profile, species }) => {
          profileMap[friendUID] = profile;

          const forms = Array.isArray(species?.forms) ? species.forms : [];

          forms.forEach((form) => {
            allForms.push({
              ...form,
              ownerUID: friendUID,
              ownerName: profile?.name || "Unknown user",
            });
          });
        });

        if (!cancelled) {
          setFriendProfiles(profileMap);
          setFriendForms(allForms.sort(defaultFirstSort));
        }
      } catch (err) {
        console.error("Failed to load friend photos:", err);
        if (!cancelled) setFriendForms([]);
      } finally {
        if (!cancelled) setLoadingFriends(false);
      }
    }

    loadFriendPhotos();

    return () => {
      cancelled = true;
    };
  }, [
    selected?.name,
    currentUID,
    isOwnCollection,
    blockRelationshipsLoading,
    selectedOwnerInteractionBlocked,
    blockedRelationshipUserIdSet,
    preloadedFriendSpeciesByName,
    preloadedFriendProfiles,
    preloadedFriendSpeciesLoading,
  ]);

  function enterEditMode() {
    if (!canEditCollectionMetadata) return;

    setAddError(null);
    setIsAddingPhoto(false);
    setAddEntries((prev) => {
      prev.forEach((e) => {
        try {
          URL.revokeObjectURL(e.previewUrl);
        } catch {}
      });
      return [];
    });

    setEditError(null);
    setEditDirty(false);
    setIsEditing(true);

    const formsToEdit = isAdminManagingCollection
      ? currentForm
        ? [currentForm]
        : []
      : currentForms || [];

    const normalized = formsToEdit.map((f) => {
      const variant = f?.variant || "";
      const notes = f?.notes || "";
      const collections = Array.isArray(f.collections) ? f.collections : [];

      const hasVariant =
        variant.trim() !== "" && variant.trim().toLowerCase() !== "na";

      const hasCollections = collections.length > 0;
      const hasNotes = notes.trim() !== "";

      return {
        ...f,
        date: normalizeDate(f?.date),
        variant,
        notes,
        collections,
        showAdditionalInfo: false,
        showVariantField: hasVariant,
        showCollectionsField: hasCollections,
        showNotesField: hasNotes,
      };
    });

    setEditForms(
      isAdminManagingCollection ? normalized : ensureSingleDefault(normalized),
    );
  }

  function exitEditMode() {
    setIsEditing(false);
    setEditDirty(false);
    setEditForms([]);
    setEditSaving(false);
    setEditError(null);
  }

  function enterAddPhotoMode() {
    if (!isOwnCollection || !isViewingOwnPhotos) return;
    setEditError(null);
    exitEditMode();
    setIsAddingPhoto(true);
    setAddError(null);
    setAddEntries((prev) => {
      prev.forEach((e) => {
        try {
          URL.revokeObjectURL(e.previewUrl);
        } catch {}
      });
      return [];
    });
  }

  function exitAddPhotoMode() {
    setIsAddingPhoto(false);
    setAddSaving(false);
    setAddError(null);
    setAddEntries((prev) => {
      prev.forEach((e) => {
        try {
          URL.revokeObjectURL(e.previewUrl);
        } catch {}
      });
      return [];
    });
  }

  function updateEditForm(idx, patch) {
    setEditDirty(true);
    setEditForms((prev) => {
      const next = prev.map((f, i) => (i === idx ? { ...f, ...patch } : f));

      return isAdminManagingCollection ? next : ensureSingleDefault(next);
    });
  }

  function setDefaultAt(idx) {
    setEditDirty(true);
    setEditForms((prev) => {
      const next = prev.map((f, i) => ({ ...f, isDefault: i === idx }));
      return ensureSingleDefault(next);
    });
    setPhotoIndex(idx);
  }

  function enterRenameMode() {
    if (!canReclassifyCollectionObservation) return;

    setIsEditing(false);
    setIsAddingPhoto(false);

    setRenameError(null);
    setRenameGeminiDialog(null);
    setRenameValue(selected?.name || "");
    setRenameCommon(selected?.common || "");
    setRenameSelectedFormIds(
      isAdminManagingCollection
        ? currentForm
          ? [currentForm.id || currentForm.url || `form-${photoIndex}`]
          : []
        : (currentForms || []).map((f, idx) => f.id || f.url || `form-${idx}`),
    );
    setIsRenaming(true);
  }

  function exitRenameMode() {
    setRenameGeminiDialog(null);
    setIsRenaming(false);
    setRenameCommon("");
    setRenameValue("");
    setRenameSaving(false);
    setRenameError(null);
    setRenameSelectedFormIds([]);
  }

  function updateRenameSuggestions(query, field) {
    const q = (query || "").toLowerCase().trim();

    if (q.length < 3) {
      setRenameSuggestions([]);
      setRenameActiveIndex(0);
      setRenameActiveField(field);
      return;
    }

    const matches = speciesNames.filter(
      (sp) =>
        sp.scientific.toLowerCase().includes(q) ||
        sp.common.toLowerCase().includes(q),
    );

    setRenameSuggestions(matches);
    setRenameActiveIndex(0);
    setRenameActiveField(field);
  }

  function selectRenameSuggestion(sp) {
    setRenameValue(sp.scientific);
    setRenameCommon(sp.common);
    setRenameSuggestions([]);
  }

  async function suggestRenameWithGemini() {
    if (!currentForm?.storagePath) {
      setRenameGeminiStatus("error");
      setRenameGeminiDialog({ accepted: false, name: "", rank: "" });
      return;
    }

    setRenameGeminiDialog(null);
    setRenameGeminiStatus("loading");
    try {
      const callable = httpsCallable(functions, "suggestSpeciesFromImage");
      const result = await callable({
        storagePath: currentForm.storagePath,
        location: displayLocation(currentForm) || "",
        locationData: formToLocationData(currentForm),
        date: currentForm?.date || null,
        formLabel: currentForm?.label || "",
      });
      const suggestion = result.data || {};
      const review = reviewGeminiSuggestion(suggestion);
      setRenameGeminiStatus(review.accepted ? "success" : "error");
      setRenameGeminiDialog({
        ...review,
        commonName: String(suggestion.commonName || "").trim(),
      });
    } catch (error) {
      console.error("Gemini identification failed:", error);
      setRenameGeminiStatus("error");
      setRenameGeminiDialog({ accepted: false, name: "", rank: "" });
    }
  }

  function acceptRenameGeminiSuggestion() {
    if (!renameGeminiDialog?.accepted) return;
    setRenameValue(renameGeminiDialog.name);
    if (renameGeminiDialog.commonName) {
      setRenameCommon(renameGeminiDialog.commonName);
    }
    setRenameSuggestions([]);
    setRenameGeminiDialog(null);
  }

  useEffect(() => {
    if (!renameGeminiDialog) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onEscape = (event) => {
      if (event.key === "Escape") setRenameGeminiDialog(null);
    };
    document.addEventListener("keydown", onEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onEscape);
    };
  }, [renameGeminiDialog]);

  function handleRenameKeyDown(e) {
    if (!renameSuggestions.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setRenameActiveIndex((i) =>
        Math.min(i + 1, renameSuggestions.length - 1),
      );
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setRenameActiveIndex((i) => Math.max(i - 1, 0));
    }

    if (e.key === "Enter") {
      e.preventDefault();
      selectRenameSuggestion(renameSuggestions[renameActiveIndex]);
    }
  }

  function openAdminDeleteObservation() {
    if (!canDeleteCollectionObservation || !currentForm || adminDeleteSaving) {
      return;
    }

    setAdminDeleteTarget({
      speciesId: selected.id,
      scientificName: selected.name || selected.id,
      commonName: selected.common || "",
      formUrl: currentForm.url,
      formLabel: currentForm.label || "Default",
      photoCount: currentForms.length,
    });

    setAdminDeleteReason("");
    setAdminDeleteError("");
  }

  function closeAdminDeleteObservation() {
    if (adminDeleteSaving) return;

    setAdminDeleteTarget(null);
    setAdminDeleteReason("");
    setAdminDeleteError("");
  }

  async function confirmAdminDeleteObservation() {
    if (
      !canDeleteCollectionObservation ||
      !adminDeleteTarget ||
      adminDeleteSaving
    ) {
      return;
    }

    const reason = adminDeleteReason.trim();

    if (reason.length < 10) {
      setAdminDeleteError(
        "Please provide a moderation reason of at least 10 characters.",
      );
      return;
    }

    setAdminDeleteSaving(true);
    setAdminDeleteError("");

    try {
      const adminDeleteObservation = httpsCallable(
        functions,
        "adminDeleteObservation",
      );

      await adminDeleteObservation({
        targetUid: dataUID,
        speciesId: adminDeleteTarget.speciesId,
        formUrl: adminDeleteTarget.formUrl,
        reason,
      });

      setAdminDeleteTarget(null);
      setAdminDeleteReason("");
      setAdminDeleteError("");

      setSelected(null);
      setPhotoIndex(0);
      setIsFullscreen(false);
      exitEditMode();
      exitRenameMode();

      updateParentSpeciesList((previousSpecies) => previousSpecies);
    } catch (error) {
      console.error("Administrator observation deletion failed:", error);

      setAdminDeleteError(
        error?.message || "The observation could not be deleted.",
      );
    } finally {
      setAdminDeleteSaving(false);
    }
  }

  function requestConfirmation({
    title,
    description,
    confirmLabel = "Confirm",
    danger = false,
  }) {
    return new Promise((resolve) => {
      setConfirmationDialog({
        title,
        description,
        confirmLabel,
        danger,
        resolve,
      });
    });
  }

  function requestCollectionName() {
    return new Promise((resolve) => {
      setCollectionNameDialog({ value: "", resolve });
    });
  }

  async function leaveEditor() {
    const nameEdited =
      isRenaming &&
      (renameValue.trim() !== String(selected?.name || "").trim() ||
        renameCommon.trim() !== String(selected?.common || "").trim());
    const pendingChanges =
      (isEditing && editDirty) ||
      (isAddingPhoto && addEntries.length > 0) ||
      nameEdited;
    if (pendingChanges) {
      const confirmed = await requestConfirmation({
        title: "Discard unsaved changes?",
        description:
          "Changes in this editor haven't been saved. Leave this section without saving them?",
        confirmLabel: "Discard changes",
        danger: true,
      });
      if (!confirmed) return;
    }
    exitEditMode();
    exitAddPhotoMode();
    exitRenameMode();
  }

  async function returnToDetailsFromRename() {
    const changed =
      renameValue.trim() !== String(selected?.name || "").trim() ||
      renameCommon.trim() !== String(selected?.common || "").trim();
    if (changed) {
      const confirmed = await requestConfirmation({
        title: "Discard identification changes?",
        description: "The scientific and common names haven't been saved.",
        confirmLabel: "Discard changes",
        danger: true,
      });
      if (!confirmed) return;
    }
    exitRenameMode();
    enterEditMode();
  }

  async function deleteFormAt(idx) {
    if (
      !isOwnCollection ||
      !isViewingOwnPhotos ||
      dataUID !== currentUID ||
      editSaving
    ) {
      return;
    }

    const form = editForms[idx];
    const isLastPhoto = editForms.length === 1;

    if (!selected?.id || !form?.url) {
      setEditError("The selected photo could not be found.");
      return;
    }

    const confirmed = await requestConfirmation({
      title: isLastPhoto ? "Delete this species?" : "Delete this photograph?",
      description: isLastPhoto
        ? "This is the last photograph. Deleting it permanently removes the entire species record."
        : "This photograph will be permanently removed from the species record.",
      confirmLabel: isLastPhoto ? "Delete species" : "Delete photograph",
      danger: true,
    });
    if (!confirmed) return;

    setEditSaving(true);
    setEditError(null);

    try {
      const deleteOwnObservation = httpsCallable(
        functions,
        "deleteOwnObservation",
      );

      const result = await deleteOwnObservation({
        speciesId: selected.id,
        formUrl: form.url,
      });

      const speciesDeleted = !!result.data?.speciesDeleted;

      if (result.data?.storageCleanupStatus === "failed") {
        console.warn(
          "The observation was removed, but its Storage file could not be cleaned up.",
        );
      }

      if (speciesDeleted) {
        updateParentSpeciesList((previousSpecies) =>
          previousSpecies.filter((species) => species.id !== selected.id),
        );

        setSelected(null);
        setIsEditing(false);
        setEditForms([]);
        setPhotoIndex(0);
        return;
      }

      const cleanedForms = ensureSingleDefault(
        editForms.filter((_, formIndex) => formIndex !== idx),
      );

      setEditForms(cleanedForms);

      setSelected((previousSelected) => ({
        ...previousSelected,
        forms: cleanedForms,
      }));

      updateParentSpeciesList((previousSpecies) =>
        previousSpecies.map((species) =>
          species.id === selected.id
            ? {
                ...species,
                forms: cleanedForms,
              }
            : species,
        ),
      );

      setPhotoIndex((currentIndex) => {
        if (cleanedForms.length === 0) return 0;

        if (currentIndex > cleanedForms.length - 1) {
          return cleanedForms.length - 1;
        }

        if (idx < currentIndex) {
          return Math.max(0, currentIndex - 1);
        }

        return currentIndex;
      });
    } catch (error) {
      console.error("Failed to delete owned observation:", error);

      setEditError(
        error?.message || "The photo could not be deleted. Please try again.",
      );
    } finally {
      setEditSaving(false);
    }
  }

  async function saveEdits() {
    if (!canEditCollectionMetadata) return;
    if (!selected?.id) return;

    setEditSaving(true);
    setEditError(null);

    try {
      if (isAdminManagingCollection) {
        const editedForm = editForms[0];

        if (!editedForm?.url) {
          throw new Error("The selected observation could not be found.");
        }

        const originalForm = (selected.forms || []).find(
          (form) => form.url === editedForm.url,
        );

        if (!originalForm) {
          throw new Error("The original observation no longer exists.");
        }

        const collections = Array.isArray(editedForm.collections)
          ? editedForm.collections
          : [];

        const existingAddedAt = editedForm.collectionAddedAt || {};

        const collectionAddedAt = {};

        collections.forEach((collectionName) => {
          collectionAddedAt[collectionName] =
            existingAddedAt[collectionName] || Date.now();
        });

        const cleanedForm = {
          id: editedForm.id || "",
          label: (editedForm.label || "Default").trim(),
          lifeformKingdom: editedForm.lifeformKingdom || "",
          url: editedForm.url || "",
          storagePath: editedForm.storagePath || "",
          date: {
            year: editedForm?.date?.year || "NA",
            month: editedForm?.date?.month || "NA",
            day: editedForm?.date?.day || "NA",
          },
          location: (
            editedForm.locationName ||
            editedForm.location ||
            ""
          ).trim(),
          locationName: (
            editedForm.locationName ||
            editedForm.location ||
            ""
          ).trim(),
          locationLat: editedForm.locationLat ?? null,
          locationLng: editedForm.locationLng ?? null,
          locationCountry: editedForm.locationCountry || "",
          locationAdminArea: editedForm.locationAdminArea || "",
          locationCity: editedForm.locationCity || "",
          locationPlaceId: editedForm.locationPlaceId || "",
          variant: (editedForm.variant || "").trim(),
          notes: (editedForm.notes || "").trim(),
          notesUpdatedAt:
            (editedForm.notes || "").trim() !==
            (originalForm.notes || "").trim()
              ? new Date()
              : editedForm.notesUpdatedAt || null,
          collections,
          collectionAddedAt,
          isDefault: !!editedForm.isDefault,
          createdAt: editedForm.createdAt || null,
        };

        let updatedForms = (selected.forms || []).map((form) =>
          form.url === cleanedForm.url
            ? cleanedForm
            : cleanedForm.isDefault
              ? { ...form, isDefault: false }
              : form,
        );

        updatedForms = ensureSingleDefault(updatedForms);

        const updatedSelected = {
          ...selected,
          forms: updatedForms,
        };

        const speciesRef = doc(db, "users", dataUID, "species", selected.id);

        const profileRef = doc(db, "profile", dataUID);
        const profileSnap = await getDoc(profileRef);
        const profileData = profileSnap.exists() ? profileSnap.data() : {};

        const now = Date.now();

        const sectionUpdatedAt = {
          ...(profileData.sectionUpdatedAt || {}),
          taxonomy: now,
          collections: now,
          quiz: now,
        };

        const adminActionRef = doc(collection(db, "adminActions"));

        const batch = writeBatch(db);

        batch.update(speciesRef, {
          forms: updatedForms,
        });

        batch.set(
          profileRef,
          {
            sectionUpdatedAt,
          },
          {
            merge: true,
          },
        );

        batch.set(adminActionRef, {
          adminUid: currentUID,
          targetUid: dataUID,
          targetName: String(profileData.name || "BioIndex user"),
          action: "update_observation_metadata",
          resourceType: "speciesObservation",
          resourcePath: `users/${dataUID}/species/${selected.id}`,
          reason: "Administrative observation assistance",
          before: observationAuditSnapshot(selected, originalForm),
          after: observationAuditSnapshot(updatedSelected, cleanedForm),
          createdAt: serverTimestamp(),
        });

        await batch.commit();

        setSelected(updatedSelected);

        updateParentSpeciesList((prev) =>
          prev.map((species) =>
            species.id === selected.id ? updatedSelected : species,
          ),
        );

        exitEditMode();
        return;
      }

      const cleanedForms = ensureSingleDefault(
        editForms.map((f, idx) => {
          const collections = Array.isArray(f.collections) ? f.collections : [];
          const existingAddedAt = f.collectionAddedAt || {};
          const collectionAddedAt = {};

          collections.forEach((collectionName) => {
            collectionAddedAt[collectionName] =
              existingAddedAt[collectionName] || Date.now();
          });

          return {
            id: f.id || "",
            label: (f.label || "Default").trim(),
            lifeformKingdom: f.lifeformKingdom || "",
            url: f.url || "",
            storagePath: f.storagePath || "",
            date: {
              year: f?.date?.year || "NA",
              month: f?.date?.month || "NA",
              day: f?.date?.day || "NA",
            },
            location: (f.locationName || f.location || "").trim(),
            locationName: (f.locationName || f.location || "").trim(),
            locationLat: f.locationLat ?? null,
            locationLng: f.locationLng ?? null,
            locationCountry: f.locationCountry || "",
            locationAdminArea: f.locationAdminArea || "",
            locationCity: f.locationCity || "",
            locationPlaceId: f.locationPlaceId || "",
            variant: (f.variant || "").trim(),
            notes: (f.notes || "").trim(),
            notesUpdatedAt:
              (f.notes || "").trim() !== (currentForms[idx]?.notes || "").trim()
                ? new Date()
                : f.notesUpdatedAt || null,
            collections,
            collectionAddedAt,
            isDefault: !!f.isDefault,
            createdAt: f.createdAt || null,
          };
        }),
      );

      await updateDoc(doc(db, "users", dataUID, "species", selected.id), {
        forms: cleanedForms,
      });

      await bumpProfileCacheMarkers(dataUID, {
        taxonomy: true,
        collections: true,
        quiz: true,
      });

      const updatedSelected = {
        ...selected,
        forms: cleanedForms,
      };

      setSelected(updatedSelected);

      updateParentSpeciesList((prev) =>
        prev.map((sp) => (sp.id === selected.id ? updatedSelected : sp)),
      );

      exitEditMode();
    } catch (err) {
      console.error(err);
      setEditError("Failed to save edits. Check Firestore rules.");
    } finally {
      setEditSaving(false);
    }
  }

  async function handleRenameSpecies() {
    if (!canReclassifyCollectionObservation) return;
    if (!selected?.id) return;

    setRenameSaving(true);
    setRenameError(null);

    const parts = renameValue.trim().split(/\s+/);

    if (parts.length < 2) {
      setRenameError("Scientific name must include genus and species.");
      setRenameSaving(false);
      return;
    }

    const selectedIds = new Set(renameSelectedFormIds);

    if (selectedIds.size === 0) {
      setRenameError("Select at least one photo to rename.");
      setRenameSaving(false);
      return;
    }

    const genusRaw = parts[0];
    const speciesRaw = parts[1];

    const genus =
      genusRaw.charAt(0).toUpperCase() + genusRaw.slice(1).toLowerCase();

    const species = speciesRaw.toLowerCase();

    const normalizedName = `${genus} ${species}`;
    const newDocId = normalizedName.trim().toLowerCase();

    try {
      if (isAdminManagingCollection) {
        if (!currentForm?.url) {
          throw new Error("The selected observation could not be found.");
        }

        setRenameSaving(false);
        const confirmed = await requestConfirmation({
          title: "Confirm identification change",
          description: `Reclassify this observation from ${selected.name || selected.id} to ${normalizedName}?`,
          confirmLabel: "Apply correction",
        });
        if (confirmed) setRenameSaving(true);

        if (!confirmed) {
          setRenameSaving(false);
          return;
        }

        const adminReclassifyObservation = httpsCallable(
          functions,
          "adminReclassifyObservation",
        );

        await adminReclassifyObservation({
          targetUid: dataUID,
          sourceSpeciesId: selected.id,
          formUrl: currentForm.url,
          scientificName: normalizedName,
          commonName: (renameCommon || "").trim(),
        });

        exitRenameMode();
        setSelected(null);
        setPhotoIndex(0);

        updateParentSpeciesList((previousSpecies) => previousSpecies);

        return;
      }

      const oldRef = doc(db, "users", dataUID, "species", selected.id);
      const oldSnap = await getDoc(oldRef);

      if (!oldSnap.exists()) {
        setRenameError("Original species not found.");
        setRenameSaving(false);
        return;
      }

      const oldData = oldSnap.data();
      const oldForms = Array.isArray(oldData.forms) ? oldData.forms : [];

      const formsToMove = oldForms.filter((form, idx) =>
        selectedIds.has(form.id || form.url || `form-${idx}`),
      );

      const formsToKeep = oldForms.filter(
        (form, idx) => !selectedIds.has(form.id || form.url || `form-${idx}`),
      );

      if (formsToMove.length === 0) {
        setRenameError("No matching photos selected.");
        setRenameSaving(false);
        return;
      }

      const movingWholeSpecies = formsToKeep.length === 0;

      if (newDocId === selected.id) {
        const updated = {
          ...selected,
          name: normalizedName,
          genus,
          common: (renameCommon || "").trim(),
        };

        setSelected(updated);

        updateParentSpeciesList((prev) =>
          prev
            .map((sp) => (sp.id === selected.id ? updated : sp))
            .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        );

        await setDoc(
          oldRef,
          {
            name: normalizedName,
            genus,
            common: (renameCommon || "").trim(),
          },
          { merge: true },
        );

        await bumpProfileCacheMarkers(dataUID, {
          taxonomy: true,
          quiz: true,
        });

        exitRenameMode();
        return;
      }

      const newRef = doc(db, "users", dataUID, "species", newDocId);
      const newSnap = await getDoc(newRef);

      let targetForms = formsToMove;

      if (newSnap.exists()) {
        const existingData = newSnap.data();
        targetForms = ensureSingleDefault([
          ...(existingData.forms || []),
          ...formsToMove,
        ]);

        await updateDoc(newRef, {
          forms: targetForms,
          name: normalizedName,
          genus,
          common: (renameCommon || "").trim(),
          userActivitySuppression: {
            type: "user_reclassification",
            createdAt: Date.now(),
          },
        });
      } else {
        targetForms = ensureSingleDefault(formsToMove);

        await setDoc(newRef, {
          name: normalizedName,
          genus,
          common: (renameCommon || "").trim(),
          owner: dataUID,
          createdAt: oldData.createdAt || new Date(),
          forms: targetForms,
          userActivitySuppression: {
            type: "user_reclassification",
            createdAt: Date.now(),
          },
        });
      }

      let updatedOld = null;

      if (movingWholeSpecies) {
        await deleteDoc(oldRef);
      } else {
        const cleanedOldForms = ensureSingleDefault(formsToKeep);

        await updateDoc(oldRef, {
          forms: cleanedOldForms,
        });

        updatedOld = {
          ...selected,
          forms: cleanedOldForms,
        };
      }

      await bumpProfileCacheMarkers(dataUID, {
        taxonomy: true,
        collections: true,
        quiz: true,
      });

      updateParentSpeciesList((prev) => {
        const withoutOld = movingWholeSpecies
          ? prev.filter((sp) => sp.id !== selected.id)
          : prev.map((sp) =>
              sp.id === selected.id ? { ...sp, forms: updatedOld.forms } : sp,
            );

        const targetSpecies = {
          id: newDocId,
          name: normalizedName,
          genus,
          common: (renameCommon || "").trim(),
          forms: targetForms,
        };

        const withoutTarget = withoutOld.filter((sp) => sp.id !== newDocId);

        return [...withoutTarget, targetSpecies].sort((a, b) =>
          (a.name || "").localeCompare(b.name || ""),
        );
      });

      if (movingWholeSpecies) {
        setSelected(null);
      } else {
        setSelected(updatedOld);
        setPhotoIndex(0);
      }

      exitRenameMode();
    } catch (err) {
      console.error(err);
      setRenameError("Failed to rename species.");
    } finally {
      setRenameSaving(false);
    }
  }

  async function addFilesFromInput(fileList) {
    const files = Array.from(fileList || []).filter(Boolean);

    if (addEntries.length + files.length > 10) {
      setFileLimitError("You can upload a maximum of 10 photos at a time.");
      return;
    }

    if (files.length === 0) return;

    setAddError(null);

    const newEntries = await Promise.all(
      files.map((file) => makeNewAddEntry(file)),
    );

    setAddEntries((prev) => [...prev, ...newEntries]);
  }

  const onDrop = (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    addFilesFromInput(acceptedFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: true,
    maxFiles: 10,
    disabled: addSaving,
    onDropRejected: () => {
      setFileLimitError("You can upload a maximum of 10 photos at a time.");
    },
  });

  function updateAddEntry(index, patch) {
    setAddEntries((prev) =>
      prev.map((e, i) => (i === index ? { ...e, ...patch } : e)),
    );
  }

  function removeAddEntry(index) {
    setAddEntries((prev) => {
      const toRemove = prev[index];
      if (toRemove?.previewUrl) {
        try {
          URL.revokeObjectURL(toRemove.previewUrl);
        } catch {}
      }
      return prev.filter((_, i) => i !== index);
    });
  }

  function setAddDefaultAt(index) {
    setAddEntries((prev) =>
      prev.map((e, i) => ({ ...e, isDefault: i === index })),
    );
  }

  async function handleAddPhotos() {
    if (!isOwnCollection || !isViewingOwnPhotos) return;
    if (!selected?.id || addEntries.length === 0) return;

    setAddSaving(true);
    setAddError(null);

    try {
      const speciesRef = doc(db, "users", dataUID, "species", selected.id);
      const docSnap = await getDoc(speciesRef);
      if (!docSnap.exists()) {
        setAddError("Species document not found.");
        return;
      }

      const data = docSnap.data();
      let existingForms = Array.isArray(data.forms) ? [...data.forms] : [];

      const genus =
        selected.genus ||
        data.genus ||
        (selected.name || "").split(/\s+/)[0] ||
        "UnknownGenus";

      const newForms = [];
      for (let i = 0; i < addEntries.length; i++) {
        const entry = addEntries[i];

        setAddEntries((prev) =>
          prev.map((e, idx) =>
            idx === i ? { ...e, uploadStatus: "uploading" } : e,
          ),
        );
        if (!entry?.file) continue;

        const objectPath = `species/${genus}/${Date.now()}_${safeFileName(
          entry.file.name,
        )}`;

        const storageRef = ref(storage, objectPath);
        await uploadBytes(storageRef, entry.file, {
          contentType: entry.file.type,
          customMetadata: {
            ownerUid: currentUID,
          },
        });

        const downloadURL = await getDownloadURL(storageRef);

        setAddEntries((prev) =>
          prev.map((e, idx) =>
            idx === i ? { ...e, uploadStatus: "success" } : e,
          ),
        );

        newForms.push({
          id: nanoid(),
          label: (entry.label || "Default").trim(),
          lifeformKingdom: entry.lifeformKingdom || "",
          url: downloadURL,
          storagePath: objectPath,
          date: {
            day: entry?.date?.day || "NA",
            month: entry?.date?.month || "NA",
            year: entry?.date?.year || "NA",
          },
          location: (entry.locationName || entry.location || "").trim(),
          locationName: (entry.locationName || entry.location || "").trim(),
          locationLat: entry.locationLat ?? null,
          locationLng: entry.locationLng ?? null,
          locationCountry: entry.locationCountry || "",
          locationAdminArea: entry.locationAdminArea || "",
          locationCity: entry.locationCity || "",
          locationPlaceId: entry.locationPlaceId || "",
          variant: (entry.variant || "NA").trim(),
          notes: (entry.notes || "").trim(),
          notesUpdatedAt: entry.notes?.trim() ? new Date() : null,
          collections: entry.collections || [],
          collectionAddedAt: Object.fromEntries(
            (entry.collections || []).map((collectionName) => [
              collectionName,
              Date.now(),
            ]),
          ),
          isDefault: !!entry.isDefault,
          createdAt: new Date(),
        });
      }

      if (newForms.length === 0) return;

      const anyNewDefault = newForms.some((f) => f?.isDefault);
      if (anyNewDefault) {
        existingForms = existingForms.map((f) => ({ ...f, isDefault: false }));
        let found = false;
        for (let i = 0; i < newForms.length; i++) {
          if (newForms[i]?.isDefault) {
            if (!found) found = true;
            else newForms[i] = { ...newForms[i], isDefault: false };
          }
        }
      }

      const updatedForms = ensureSingleDefault([...existingForms, ...newForms]);

      await updateDoc(speciesRef, { forms: updatedForms });

      await bumpProfileCacheMarkers(dataUID, {
        taxonomy: true,
        collections: true,
        quiz: true,
      });

      const updatedSelected = { ...selected, forms: updatedForms };
      setSelected(updatedSelected);

      updateParentSpeciesList((prev) =>
        prev.map((sp) => (sp.id === selected.id ? updatedSelected : sp)),
      );

      exitAddPhotoMode();
      setPhotoIndex(0);
    } catch (err) {
      console.error(err);

      setAddEntries((prev) =>
        prev.map((e) =>
          e.uploadStatus === "uploading" ? { ...e, uploadStatus: "error" } : e,
        ),
      );

      setAddError("Some uploads failed.");
    } finally {
      setAddSaving(false);
    }
  }

  useEffect(() => {
    return () => {
      addEntries.forEach((e) => {
        try {
          URL.revokeObjectURL(e.previewUrl);
        } catch {}
      });
    };
  }, [addEntries]);

  useEffect(() => {
    async function loadCollections() {
      const collectionOwnerUid = isAdminManagingCollection
        ? dataUID
        : currentUID;

      if (!collectionOwnerUid) return;

      const snap = await getDocs(
        collection(db, "users", collectionOwnerUid, "species"),
      );

      const set = new Set();

      snap.forEach((doc) => {
        const forms = doc.data().forms || [];
        forms.forEach((f) => {
          (f.collections || []).forEach((c) => set.add(c));
        });
      });

      setCollectionsList(Array.from(set).sort());
    }

    loadCollections();
  }, [currentUID, dataUID, selected, isAdminManagingCollection]);

  if (
    !selected ||
    blockRelationshipsLoading ||
    selectedOwnerInteractionBlocked
  ) {
    return null;
  }

  return (
    <>
      <div
        className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto overscroll-contain bg-[#202a30]/70 px-0 pt-0 pb-0 sm:items-center sm:px-4 sm:py-5"
        onClick={() => {
          if (
            isEditing ||
            isAddingPhoto ||
            isRenaming ||
            editSaving ||
            addSaving ||
            renameSaving
          )
            return;
          setSelected(null);
          setIsFullscreen(false);
          setPhotoIndex(0);
        }}
      >
        {" "}
        <div
          onClick={(e) => e.stopPropagation()}
          className="bioindex-species-modal-shell relative w-full"
        >
          <div
            className={`bioindex-species-modal ${isRenaming ? "bioindex-species-modal--identification" : isEditing ? "bioindex-species-modal--edit" : isAddingPhoto ? "bioindex-species-modal--add" : "bioindex-species-modal--view"} relative flex flex-col bg-white rounded-none sm:rounded-[1.4rem] shadow-2xl w-screen sm:w-auto max-w-none h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-hidden border border-[#93CA87]/30`}
            style={{
              "--bioindex-species-photo-width": `${desktopPhotoWidth + 48}px`,
            }}
          >
            <header
              className="bioindex-species-actionbar"
              aria-label="Species record actions"
            >
              {(isEditing || isAddingPhoto || isRenaming) && (
                <div className="bioindex-species-actionbar-label">
                  <span className="bioindex-species-actionbar-title">
                    {isAddingPhoto
                      ? "Add photos"
                      : isRenaming
                        ? "Edit identification"
                        : "Edit species"}
                  </span>
                </div>
              )}
              {isEditing || isAddingPhoto || isRenaming ? (
                <button
                  type="button"
                  className="bioindex-species-action bioindex-species-action-exit"
                  disabled={editSaving || addSaving || renameSaving}
                  onClick={leaveEditor}
                  aria-label="Back to species record"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                  <span>Back</span>
                </button>
              ) : (
                <div className="bioindex-species-actionbar-buttons">
                  {isOwnCollection && isViewingOwnPhotos && (
                    <button
                      type="button"
                      className="bioindex-species-action bioindex-species-action-add"
                      onClick={enterAddPhotoMode}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="3" />
                        <path d="M8 14l2-2 3 3 2-2 3 3" />
                        <path d="M12 6v5M9.5 8.5h5" />
                      </svg>
                      <span>Add photo</span>
                    </button>
                  )}
                  {canEditCollectionMetadata && (
                    <button
                      type="button"
                      className="bioindex-species-action bioindex-species-action-edit"
                      onClick={enterEditMode}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 20h9" />
                        <path d="M16.6 3.5a2.1 2.1 0 0 1 3 3L8.7 17.4 4 18.5l1.1-4.7L16.6 3.5Z" />
                      </svg>
                      <span>
                        {isAdminManagingCollection ? "Admin edit" : "Edit"}
                      </span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="bioindex-species-action bioindex-species-action-exit"
                    onClick={() => {
                      setSelected(null);
                      setIsFullscreen(false);
                      setPhotoIndex(0);
                      exitEditMode();
                      exitAddPhotoMode();
                      exitRenameMode();
                    }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                    <span>Exit</span>
                  </button>
                </div>
              )}
            </header>
            {(isEditing || isRenaming) && (
              <nav
                className="bioindex-species-editor-tabs"
                aria-label="Edit species sections"
              >
                <button
                  type="button"
                  className={
                    isEditing
                      ? "bioindex-species-editor-tab is-active"
                      : "bioindex-species-editor-tab"
                  }
                  aria-current={isEditing ? "page" : undefined}
                  disabled={editSaving || renameSaving}
                  onClick={() => {
                    if (isEditing) return;
                    returnToDetailsFromRename();
                  }}
                >
                  Photos & details
                </button>
                {canReclassifyCollectionObservation && (
                  <button
                    type="button"
                    className={
                      isRenaming
                        ? "bioindex-species-editor-tab is-active"
                        : "bioindex-species-editor-tab"
                    }
                    aria-current={isRenaming ? "page" : undefined}
                    disabled={editSaving || renameSaving}
                    onClick={async () => {
                      if (isRenaming) return;
                      if (editDirty) {
                        const confirmed = await requestConfirmation({
                          title: "Discard unsaved details?",
                          description:
                            "Save your observation edits before changing the identification, or discard the unsaved details.",
                          confirmLabel: "Discard changes",
                          danger: true,
                        });
                        if (!confirmed) return;
                      }
                      enterRenameMode();
                    }}
                  >
                    Identification
                  </button>
                )}
              </nav>
            )}
            <div className="bioindex-species-body min-h-0 flex-1 overflow-y-auto p-3 pb-[calc(22px+env(safe-area-inset-bottom,0px))] sm:p-6">
              {" "}
              {editError && isEditing && (
                <div className="bioindex-species-inline-error">{editError}</div>
              )}
              {fileLimitError && isAddingPhoto && (
                <div className="bioindex-species-inline-error">
                  {fileLimitError}
                </div>
              )}
              {addError && isAddingPhoto && (
                <div className="bioindex-species-inline-error">{addError}</div>
              )}
              {!isEditing && !isAddingPhoto && !isRenaming && (
                <div className="flex justify-center mb-4 sm:mb-6">
                  {viewMode === "friends" && loadingFriends}

                  <div
                    className="relative w-full flex items-center justify-center bg-transparent"
                    onClick={() => {
                      if (!isEditing && !isAddingPhoto) setIsFullscreen(true);
                    }}
                  >
                    {!isAddingPhoto && !isEditing && (
                      <div className="relative inline-block max-w-full">
                        <img
                          key={currentPhotoSrc}
                          src={currentPhotoSrc}
                          alt={selected.name || "Species"}
                          onError={() => {
                            console.error(
                              "Modal image failed to load:",
                              selected?.name,
                            );
                          }}
                          className="block object-contain max-w-full max-h-[62vh] cursor-zoom-in"
                          onLoad={(event) => {
                            const image = event.currentTarget;
                            if (
                              image.naturalWidth > 0 &&
                              image.naturalHeight > 0
                            ) {
                              setPhotoDimensions({
                                src: currentPhotoSrc,
                                width: image.naturalWidth,
                                height: image.naturalHeight,
                              });
                            }
                          }}
                        />

                        {isOwnCollection &&
                          isViewingOwnPhotos &&
                          !isRenaming &&
                          currentPhotoSrc && (
                            <button
                              type="button"
                              title="Create share card"
                              aria-label="Create share card"
                              style={{ display: "none" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShareCardOpen(true);
                              }}
                              className="absolute bottom-3 right-3 z-30 flex h-9 w-9 items-center justify-center rounded-full border border-white/30 bg-black/55 text-white shadow-md backdrop-blur-md transition hover:bg-black/70 hover:scale-105 active:scale-95"
                            >
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="h-[18px] w-[18px]"
                                aria-hidden="true"
                              >
                                <rect
                                  x="3"
                                  y="3"
                                  width="18"
                                  height="18"
                                  rx="5"
                                />
                                <circle cx="12" cy="12" r="4" />
                                <circle
                                  cx="17.4"
                                  cy="6.6"
                                  r="0.7"
                                  fill="currentColor"
                                  stroke="none"
                                />
                              </svg>
                            </button>
                          )}

                        {currentForms.length > 1 &&
                          !isEditing &&
                          !isAddingPhoto &&
                          !isRenaming && (
                            <>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (photoIndex === 0) return;
                                  setPhotoIndex((i) => Math.max(0, i - 1));
                                }}
                                className={`absolute left-2 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full backdrop-blur-md border border-white/20 flex items-center justify-center transition ${
                                  photoIndex === 0
                                    ? "bg-white/5 opacity-20 cursor-default"
                                    : "bg-black/20 text-white hover:bg-black/35 active:scale-95"
                                }`}
                              >
                                ‹
                              </button>

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (photoIndex === currentForms.length - 1)
                                    return;
                                  setPhotoIndex((i) =>
                                    Math.min(currentForms.length - 1, i + 1),
                                  );
                                }}
                                className={`absolute right-2 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full backdrop-blur-md border border-white/20 flex items-center justify-center transition ${
                                  photoIndex === currentForms.length - 1
                                    ? "bg-white/5 opacity-20 cursor-default"
                                    : "bg-black/20 text-white hover:bg-black/35 active:scale-95"
                                }`}
                              >
                                ›
                              </button>
                            </>
                          )}
                      </div>
                    )}
                    {viewMode === "friends" &&
                      currentForm?.ownerUID &&
                      friendProfiles[currentForm.ownerUID]?.photoURL &&
                      loadedFriendIcons[currentForm.ownerUID] && (
                        <div className="absolute top-2 left-2">
                          <img
                            src={friendProfiles[currentForm.ownerUID].photoURL}
                            alt="friend"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.location.href = `/profile/${currentForm.ownerUID}`;
                            }}
                            className="w-10 h-10 rounded-full border-2 border-white shadow-md object-cover cursor-pointer transition-transform duration-200 hover:scale-110"
                          />
                        </div>
                      )}
                  </div>
                </div>
              )}
              {isFullscreen && (
                <div
                  className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center"
                  onClick={() => setIsFullscreen(false)}
                >
                  {/* IMAGE */}
                  <img
                    src={currentPhotoSrc}
                    alt={selected.name || "Species"}
                    className="max-w-[95vw] max-h-[95vh] object-contain cursor-zoom-out"
                    onClick={(e) => e.stopPropagation()}
                  />

                  {/* LEFT ARROW */}
                  {currentForms.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (photoIndex === 0) return;
                        setPhotoIndex((i) => i - 1);
                      }}
                      className={`absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 
  w-12 h-12 sm:w-14 sm:h-14 
  flex items-center justify-center 
  rounded-full backdrop-blur-md border border-white/20
  transition-all duration-200
  ${
    photoIndex === 0
      ? "bg-white/5 opacity-30 cursor-default"
      : "bg-white/10 hover:bg-white/20 hover:scale-105 active:scale-95 cursor-pointer"
  }`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>
                  )}

                  {/* RIGHT ARROW */}
                  {currentForms.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (photoIndex === currentForms.length - 1) return;
                        setPhotoIndex((i) => i + 1);
                      }}
                      className={`absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 
  w-12 h-12 sm:w-14 sm:h-14 
  flex items-center justify-center 
  rounded-full backdrop-blur-md border border-white/20
  transition-all duration-200
  ${
    photoIndex === currentForms.length - 1
      ? "bg-white/5 opacity-30 cursor-default"
      : "bg-white/10 hover:bg-white/20 hover:scale-105 active:scale-95 cursor-pointer"
  }`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              )}
              {!isEditing && !isAddingPhoto && !isRenaming && (
                <div className="mt-5 px-1 sm:px-2">
                  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                    <div>
                      <h3
                        className="text-3xl italic text-slate-900 leading-tight"
                        style={{ fontFamily: "Cormorant Garamond" }}
                      >
                        {selected.name || "—"}
                      </h3>

                      <p className="mt-0.5 text-slate-600">
                        {selected.common || ""}
                      </p>
                    </div>

                    {!isAddingPhoto && !isRenaming && (
                      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                        {currentForms.length > 1 && (
                          <div className="text-xs text-slate-500 bg-white/70 border border-white rounded-full px-3 py-1 shadow-sm">
                            Photo {photoIndex + 1} of {currentForms.length}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {isOwnCollection && friendForms.length > 0 && (
                    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#93CA87]/25 pt-3">
                      <span className="mr-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                        Also photographed by
                      </span>

                      <button
                        type="button"
                        title="My photos"
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewMode("mine");
                          setSelectedFriendUID(null);
                          setPhotoIndex(0);
                        }}
                        className={`h-8 w-8 overflow-hidden rounded-full border-2 shadow-sm flex items-center justify-center text-xs font-medium transition ${
                          viewMode === "mine"
                            ? "border-[#24C3BE] bg-[#374049] text-white ring-2 ring-[#24C3BE]/20"
                            : "border-white bg-white text-slate-600 hover:scale-105"
                        }`}
                      >
                        {friendProfiles[currentUID]?.photoURL ? (
                          <img
                            src={friendProfiles[currentUID].photoURL}
                            alt="My photos"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          "Me"
                        )}
                      </button>

                      {friendOwners.slice(0, 5).map((friendForm) => {
                        const profile = friendProfiles[friendForm.ownerUID];
                        const photoURL = profile?.photoURL;
                        const initial = (friendForm.ownerName || "?")
                          .charAt(0)
                          .toUpperCase();

                        return (
                          <button
                            key={friendForm.ownerUID}
                            type="button"
                            title={friendForm.ownerName || "Friend photos"}
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewMode("friends");
                              setSelectedFriendUID(friendForm.ownerUID);
                              setPhotoIndex(0);
                            }}
                            className={`h-8 w-8 overflow-hidden rounded-full border-2 shadow-sm flex items-center justify-center text-xs font-medium transition ${
                              viewMode === "friends" &&
                              selectedFriendUID === friendForm.ownerUID
                                ? "border-[#24C3BE] bg-[#374049] text-white ring-2 ring-[#24C3BE]/20"
                                : "border-white bg-white text-slate-600 hover:scale-105"
                            }`}
                          >
                            {photoURL ? (
                              <img
                                src={photoURL}
                                alt={friendForm.ownerName || "Friend"}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              initial
                            )}
                          </button>
                        );
                      })}

                      {friendOwners.length > 5 && (
                        <div
                          title={`${friendOwners.length - 5} more friends`}
                          className="flex h-8 min-w-8 items-center justify-center rounded-full border-2 border-white bg-[#374049] px-1.5 text-[10px] font-semibold text-white shadow-sm"
                        >
                          +{friendOwners.length - 5}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {isRenaming && (
                <div className="bioindex-species-editor bioindex-species-rename mt-2 mb-6 w-full space-y-5">
                  <div className="bioindex-species-panel-heading">
                    <div>
                      <p className="bioindex-species-section-kicker">
                        TAXONOMIC IDENTIFICATION
                      </p>
                      <h2>Edit identification</h2>
                      <p>
                        Correct the scientific or common name. Choose which
                        photographs the change applies to.
                      </p>
                    </div>
                  </div>
                  {renameError && (
                    <div className="bioindex-species-inline-error">
                      {renameError}
                    </div>
                  )}

                  <div className="relative">
                    <label
                      className="bioindex-species-field-label"
                      htmlFor="bioindex-species-scientific-name"
                    >
                      Scientific name
                    </label>
                    <input
                      id="bioindex-species-scientific-name"
                      type="text"
                      value={renameValue}
                      onChange={(e) => {
                        setRenameValue(e.target.value);
                        updateRenameSuggestions(e.target.value, "scientific");
                      }}
                      onKeyDown={handleRenameKeyDown}
                      className="w-full border border-slate-300 rounded-xl px-4 py-2"
                      placeholder="Genus species"
                      disabled={renameSaving}
                    />

                    {renameActiveField === "scientific" &&
                      renameSuggestions.length > 0 && (
                        <div className="absolute top-full left-0 z-20 w-full border border-slate-200 rounded-xl mt-1 bg-white max-h-40 overflow-y-auto shadow-sm">
                          {renameSuggestions.map((sp, i) => (
                            <div
                              key={sp.scientific}
                              onClick={() => selectRenameSuggestion(sp)}
                              className={`px-3 py-2 text-sm cursor-pointer ${
                                renameActiveIndex === i
                                  ? "bg-slate-200"
                                  : "hover:bg-slate-100"
                              }`}
                            >
                              <span className="italic">{sp.scientific}</span>
                              <span className="text-slate-500 ml-2">
                                — {sp.common}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                  <div className="relative">
                    <label
                      className="bioindex-species-field-label"
                      htmlFor="bioindex-species-common-name"
                    >
                      Common name
                    </label>
                    <input
                      id="bioindex-species-common-name"
                      type="text"
                      value={renameCommon}
                      onChange={(e) => {
                        setRenameCommon(e.target.value);
                        updateRenameSuggestions(e.target.value, "common");
                      }}
                      onKeyDown={handleRenameKeyDown}
                      className="w-full border border-slate-300 rounded-xl px-4 py-2"
                      placeholder="Common name"
                      disabled={renameSaving}
                    />

                    {renameActiveField === "common" &&
                      renameSuggestions.length > 0 && (
                        <div className="absolute top-full left-0 z-20 w-full border border-slate-200 rounded-xl mt-1 bg-white max-h-40 overflow-y-auto shadow-sm">
                          {renameSuggestions.map((sp, i) => (
                            <div
                              key={sp.scientific}
                              onClick={() => selectRenameSuggestion(sp)}
                              className={`px-3 py-2 text-sm cursor-pointer ${
                                renameActiveIndex === i
                                  ? "bg-slate-200"
                                  : "hover:bg-slate-100"
                              }`}
                            >
                              <span className="italic">{sp.scientific}</span>
                              <span className="text-slate-500 ml-2">
                                — {sp.common}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>

                  <div className="bioindex-species-identification-tools flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={suggestRenameWithGemini}
                      disabled={
                        renameSaving || renameGeminiStatus === "loading"
                      }
                      className="bioindex-species-identification-tool inline-flex w-auto items-center justify-center rounded-lg border border-[#93ca87]/40 bg-white px-3 py-2 text-xs font-semibold text-[#374049] shadow-sm transition hover:border-[#24c3be]/60 hover:bg-[#24c3be]/5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="flex items-center justify-center gap-2">
                        <img
                          src={geminiLogo}
                          alt=""
                          className="h-4 w-4 object-contain"
                        />
                        <span>
                          {renameGeminiStatus === "loading"
                            ? "Consulting Gemini..."
                            : "Suggest ID with Gemini"}
                        </span>
                      </span>
                    </button>

                    {renameValue?.trim() && (
                      <a
                        href={`https://www.inaturalist.org/search?q=${encodeURIComponent(
                          renameValue.trim(),
                        )}`}
                        target="_blank"
                        rel="noreferrer"
                        className="bioindex-species-identification-tool inline-flex w-auto items-center justify-center rounded-lg border border-[#93ca87]/40 bg-white px-3 py-2 text-xs font-semibold text-[#374049] shadow-sm transition hover:border-[#24c3be]/60 hover:bg-[#24c3be]/5"
                      >
                        <span className="flex items-center justify-center gap-2">
                          <img
                            src={inatLogo}
                            alt=""
                            className="h-4 w-4 object-contain"
                          />
                          <span>Check on iNaturalist ↗</span>
                        </span>
                      </a>
                    )}
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-3">
                    <div className="text-xs font-semibold text-slate-600 mb-2">
                      Photos affected by rename
                    </div>

                    <div className="bioindex-species-rename-photo-grid grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {currentForms.map((form, idx) => {
                        const formKey = form.id || form.url || `form-${idx}`;
                        const checked = renameSelectedFormIds.includes(formKey);

                        return (
                          <button
                            key={formKey}
                            type="button"
                            onClick={() => {
                              if (isAdminManagingCollection) return;

                              setRenameSelectedFormIds((prev) =>
                                prev.includes(formKey)
                                  ? prev.filter((id) => id !== formKey)
                                  : [...prev, formKey],
                              );
                            }}
                            className={`relative rounded-xl border p-2 text-left transition ${
                              checked
                                ? "border-[#1f3d2b] bg-[#1f3d2b]/10"
                                : "border-slate-200 bg-slate-50 hover:bg-slate-100"
                            }`}
                            disabled={renameSaving || isAdminManagingCollection}
                          >
                            <div className="bioindex-species-rename-photo-preview flex w-full items-center justify-center overflow-hidden rounded-lg border border-[#d7e6d5] bg-[#f8faf8] p-1">
                              <img
                                src={form.url}
                                alt=""
                                className="block max-h-[240px] max-w-full h-auto w-auto object-contain"
                              />
                            </div>

                            <div className="mt-1 text-[11px] text-slate-600 truncate">
                              {form.label || "Photo"} {idx + 1}
                            </div>

                            {checked && (
                              <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#1f3d2b] text-white text-[11px] flex items-center justify-center">
                                ✓
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="bioindex-species-panel-actions bioindex-species-rename-footer">
                    <button
                      type="button"
                      onClick={handleRenameSpecies}
                      disabled={renameSaving}
                      className={`rounded-md px-2.5 py-1.5 text-[11px] font-medium text-white shadow-sm transition disabled:opacity-60 ${
                        renameSaving
                          ? "bg-slate-400"
                          : "bg-[#1f3d2b] hover:bg-[#162c20]"
                      }`}
                    >
                      {renameSaving
                        ? isAdminManagingCollection
                          ? "Reclassifying..."
                          : "Renaming..."
                        : isAdminManagingCollection
                          ? "Apply Taxonomic Correction"
                          : "Save identification"}
                    </button>

                    <button
                      type="button"
                      onClick={returnToDetailsFromRename}
                      disabled={renameSaving}
                      className="rounded-md border border-[#93ca87]/40 bg-white px-2.5 py-1.5 text-[11px] font-medium text-[#374049] shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
                    >
                      Back to details
                    </button>
                  </div>
                </div>
              )}
              {!isEditing && !isAddingPhoto && !isRenaming && (
                <div className="mt-4 px-1 sm:px-2">
                  <div className="rounded-2xl bg-[#f4f6f8] sm:bg-white/70 border border-white shadow-sm p-4">
                    {" "}
                    {displayLocation(currentForm) &&
                      displayLocation(currentForm) !== "Unknown" && (
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 text-slate-400">⌖</div>
                          <div className="w-0 min-w-0 flex-1">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                              {" "}
                              Location
                            </div>
                            <div className="max-w-full break-words text-sm leading-snug text-slate-800 [overflow-wrap:anywhere]">
                              {displayLocation(currentForm)}
                              {hasMappedLocation(currentForm) && (
                                <span className="ml-2 inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 px-2 py-[1px] text-[10px] font-medium text-emerald-700">
                                  ✓
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {formatDateForDisplay(currentForm?.date) !==
                        "Unknown" && (
                        <div className="rounded-full bg-[#374049] border border-[#93CA87]/60 px-3 py-1.5 text-xs text-white shadow-sm">
                          <span className="text-[#24C3BE]">Date</span>{" "}
                          <span className="mx-1.5 text-slate-300">•</span>
                          {formatDateForDisplay(currentForm?.date)}
                        </div>
                      )}

                      {currentForm?.label &&
                        currentForm.label.trim().toLowerCase() !==
                          "default" && (
                          <div className="rounded-full bg-[#374049] border border-[#93CA87]/60 px-3 py-1.5 text-xs text-white shadow-sm">
                            <span className="text-[#24C3BE]">Lifeform</span>{" "}
                            <span className="mx-1.5 text-[#93CA87]">•</span>{" "}
                            {currentForm.label}
                          </div>
                        )}

                      {currentForm?.variant && currentForm.variant !== "NA" && (
                        <div className="rounded-full bg-[#374049] border border-[#93CA87]/60 px-3 py-1.5 text-xs text-white shadow-sm">
                          <span className="text-[#24C3BE]">Variant</span>{" "}
                          <span className="mx-1.5 text-slate-300">•</span>
                          {currentForm.variant}
                        </div>
                      )}

                      {Array.isArray(currentForm?.collections) &&
                        currentForm.collections.length > 0 && (
                          <div className="relative">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCollectionsPopoverOpen((open) => !open);
                              }}
                              className="max-w-full break-words rounded-full bg-[#374049] border border-[#93CA87]/60 px-3 py-1.5 text-xs text-white shadow-sm"
                            >
                              <span className="text-[#24C3BE]">
                                {" "}
                                Collections
                              </span>
                            </button>

                            {collectionsPopoverOpen && (
                              <div
                                className="absolute right-0 bottom-full z-40 mb-2 w-max max-w-[16rem] rounded-2xl border border-[#e7ddcf] bg-white p-3 text-xs text-slate-700 shadow-xl"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="flex w-max max-w-full flex-wrap gap-1.5">
                                  {" "}
                                  {currentForm.collections.map(
                                    (collectionName, collectionIndex) => (
                                      <span
                                        key={`${collectionName}-${collectionIndex}`}
                                        className="rounded-full bg-[#f4f6f8] border border-[#e7ddcf] px-2 py-1"
                                      >
                                        {collectionName}
                                      </span>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                      {viewMode === "friends" && currentForm?.ownerName && (
                        <div className="rounded-full bg-[#374049] border border-[#93CA87]/60 px-3 py-1.5 text-xs text-white shadow-sm">
                          <span className="text-[#24C3BE]">Owner</span>{" "}
                          <span className="mx-1.5 text-slate-300">•</span>
                          {currentForm.ownerName}
                        </div>
                      )}
                    </div>
                    <PlantSpeciesDescription
                      scientificName={selected.name}
                      enabled={isPlantSpecies}
                      detailButton
                    />
                    {currentForm?.notes?.trim() && (
                      <div className="mt-4 border-t border-[#93CA87]/30 pt-4">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 shrink-0 text-[#24C3BE]">
                            ✎
                          </div>

                          <div className="w-0 min-w-0 flex-1">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                              Notes
                            </div>

                            <p className="mt-1 max-w-full whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700 [overflow-wrap:anywhere]">
                              {currentForm.notes}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    <EcologicalRelationships
                      scientificName={selected.name}
                      commonName={selected.common || ""}
                      kingdom={kingdomName}
                      collectionOwnerUid={dataUID}
                    />
                  </div>
                </div>
              )}
              {isEditing && (
                <div className="bioindex-species-editor mx-auto mt-2 w-full max-w-5xl space-y-6">
                  <div className="bioindex-species-panel-heading">
                    <div className="min-w-0">
                      <p className="bioindex-species-section-kicker">
                        OBSERVATION EDITOR
                      </p>
                      <h2>Photos & details</h2>
                      <p>
                        Update the photographs and observation information for{" "}
                        <em>{selected.name || "this species"}</em>.
                      </p>
                    </div>
                    <div className="bioindex-species-panel-actions">
                      <button
                        type="button"
                        className="bioindex-species-btn-primary"
                        onClick={saveEdits}
                        disabled={editSaving}
                      >
                        {editSaving ? "Saving changes..." : "Save changes"}
                      </button>
                      <button
                        type="button"
                        className="bioindex-species-btn-secondary"
                        onClick={leaveEditor}
                        disabled={editSaving}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                  {canDeleteCollectionObservation && (
                    <div className="bioindex-species-admin-row">
                      <span>Administrative moderation</span>
                      <button
                        type="button"
                        className="bioindex-species-btn-danger"
                        onClick={openAdminDeleteObservation}
                        disabled={adminDeleteSaving}
                      >
                        Delete observation
                      </button>
                    </div>
                  )}
                  <div className="space-y-10">
                    {editForms.map((f, idx) => (
                      <div
                        key={`${f.id || "form"}-${idx}`}
                        className="bioindex-species-form-card flex flex-col gap-5 md:flex-row"
                      >
                        {/* ================= LEFT COLUMN: IMAGE + ACTIONS ================= */}
                        <div className="flex w-full flex-col md:w-56">
                          <div className="relative">
                            <img
                              src={f.url}
                              alt={f.label || "Lifeform"}
                              onClick={() => setPhotoIndex(idx)}
                              className={`h-56 w-full cursor-pointer rounded-xl object-cover border md:h-40 ${
                                f.isDefault
                                  ? "border-2 border-[#24c3be]"
                                  : "border-[#93ca87]/40"
                              }`}
                            />
                          </div>

                          <div className="mt-3 flex items-center justify-between gap-3">
                            <label className="flex items-center gap-2 text-xs text-slate-700">
                              <input
                                type="radio"
                                name="defaultForm"
                                checked={!!f.isDefault}
                                onChange={() => setDefaultAt(idx)}
                                disabled={editSaving}
                              />
                              Main Photo
                            </label>

                            {isOwnCollection && (
                              <button
                                type="button"
                                onClick={() => deleteFormAt(idx)}
                                disabled={editSaving}
                                className="rounded bg-black/70 px-2 py-1 text-xs text-white disabled:opacity-50"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>

                        {/* ================= RIGHT COLUMN: METADATA ================= */}
                        <div className="min-w-0 flex-1 space-y-4">
                          {/* ================= DATE + LOCATION ================= */}
                          <div className="grid min-w-0 grid-cols-1 items-end gap-4 md:grid-cols-[2fr_3fr]">
                            {/* -------- DATE -------- */}
                            <div className="flex h-full min-w-0 flex-col">
                              <div className="mb-1 text-xs font-semibold text-slate-600">
                                Date
                              </div>

                              <div className="mt-auto grid grid-cols-[0.9fr_1.6fr_1.1fr] gap-2">
                                <select
                                  value={f.date?.day || "NA"}
                                  onChange={(e) =>
                                    updateEditForm(idx, {
                                      date: {
                                        ...normalizeDate(f.date),
                                        day: e.target.value,
                                      },
                                    })
                                  }
                                  className="h-[48px] w-full rounded-xl border border-[#93ca87]/40 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                  disabled={editSaving}
                                >
                                  {DAYS.map((d) => (
                                    <option key={d} value={d}>
                                      {d}
                                    </option>
                                  ))}
                                </select>

                                <select
                                  value={f.date?.month || "NA"}
                                  onChange={(e) =>
                                    updateEditForm(idx, {
                                      date: {
                                        ...normalizeDate(f.date),
                                        month: e.target.value,
                                      },
                                    })
                                  }
                                  className="h-[48px] w-full rounded-xl border border-[#93ca87]/40 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                  disabled={editSaving}
                                >
                                  {MONTHS.map((m) => (
                                    <option key={m.value} value={m.value}>
                                      {m.label}
                                    </option>
                                  ))}
                                </select>

                                <select
                                  value={f.date?.year || "NA"}
                                  onChange={(e) =>
                                    updateEditForm(idx, {
                                      date: {
                                        ...normalizeDate(f.date),
                                        year: e.target.value,
                                      },
                                    })
                                  }
                                  className="h-[48px] w-full rounded-xl border border-[#93ca87]/40 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                  disabled={editSaving}
                                >
                                  {YEARS.map((y) => (
                                    <option key={y} value={y}>
                                      {y}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            {/* -------- LOCATION -------- */}
                            <div className="flex h-full min-w-0 flex-col">
                              <div className="mb-1 text-xs font-semibold text-slate-600">
                                Location
                              </div>

                              <button
                                type="button"
                                onClick={() =>
                                  setLocationModal({
                                    mode: "edit",
                                    index: idx,
                                  })
                                }
                                disabled={editSaving}
                                title={
                                  displayLocation(f) || "Select location on map"
                                }
                                className="mt-auto block h-[48px] w-full min-w-0 overflow-hidden rounded-xl border border-[#93ca87]/40 bg-white px-4 text-left text-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25 disabled:opacity-50"
                              >
                                <span className="block w-full truncate">
                                  {displayLocation(f) ||
                                    "Select location on map"}
                                </span>
                              </button>
                            </div>
                          </div>

                          {/* ================= FORM + ADDITIONAL INFORMATION ================= */}
                          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[2fr_3fr]">
                            <LifeformPicker
                              scientificName={selected?.name || ""}
                              value={f.label || ""}
                              selectedKingdom={f.lifeformKingdom || ""}
                              onChange={(value) =>
                                updateEditForm(idx, { label: value })
                              }
                              onKingdomChange={(value) =>
                                updateEditForm(idx, {
                                  lifeformKingdom: value,
                                  label: "",
                                })
                              }
                              disabled={editSaving}
                            />

                            {/* -------- ADDITIONAL INFORMATION SELECTOR -------- */}
                            <div className="relative min-w-0">
                              <div className="mb-1 text-xs font-semibold text-slate-600">
                                Additional Information
                              </div>

                              <button
                                type="button"
                                onClick={() =>
                                  updateEditForm(idx, {
                                    showAdditionalInfo: !f.showAdditionalInfo,
                                  })
                                }
                                className="flex h-[48px] w-full min-w-0 items-center justify-between gap-3 rounded-xl border border-[#93ca87]/40 bg-white px-4 text-left text-sm text-[#374049] transition hover:bg-[#24c3be]/5 focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                disabled={editSaving}
                              >
                                <span className="truncate">
                                  {f.showVariantField ||
                                  f.showCollectionsField ||
                                  f.showNotesField
                                    ? "Manage optional fields"
                                    : "Choose optional fields"}
                                </span>

                                <span
                                  className={`shrink-0 text-lg text-[#24c3be] transition-transform ${
                                    f.showAdditionalInfo ? "rotate-180" : ""
                                  }`}
                                >
                                  ⌄
                                </span>
                              </button>

                              {f.showAdditionalInfo && (
                                <div className="absolute left-0 top-full z-30 mt-2 w-full rounded-2xl border border-[#93ca87]/40 bg-white p-3 shadow-lg">
                                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        updateEditForm(idx, {
                                          showVariantField: !f.showVariantField,
                                        })
                                      }
                                      className={`rounded-xl border px-3 py-2 text-xs font-medium transition ${
                                        f.showVariantField
                                          ? "border-[#24c3be] bg-[#24c3be] text-white"
                                          : "border-[#93ca87]/40 bg-white text-[#374049] hover:bg-[#24c3be]/10"
                                      }`}
                                    >
                                      {f.showVariantField
                                        ? "✓ Variant"
                                        : "+ Variant"}
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() =>
                                        updateEditForm(idx, {
                                          showCollectionsField:
                                            !f.showCollectionsField,
                                        })
                                      }
                                      className={`rounded-xl border px-3 py-2 text-xs font-medium transition ${
                                        f.showCollectionsField
                                          ? "border-[#24c3be] bg-[#24c3be] text-white"
                                          : "border-[#93ca87]/40 bg-white text-[#374049] hover:bg-[#24c3be]/10"
                                      }`}
                                    >
                                      {f.showCollectionsField
                                        ? "✓ Collections"
                                        : "+ Collections"}
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() =>
                                        updateEditForm(idx, {
                                          showNotesField: !f.showNotesField,
                                        })
                                      }
                                      className={`rounded-xl border px-3 py-2 text-xs font-medium transition ${
                                        f.showNotesField
                                          ? "border-[#24c3be] bg-[#24c3be] text-white"
                                          : "border-[#93ca87]/40 bg-white text-[#374049] hover:bg-[#24c3be]/10"
                                      }`}
                                    >
                                      {f.showNotesField ? "✓ Notes" : "+ Notes"}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* ================= SELECTED OPTIONAL FIELDS ================= */}
                          {(f.showVariantField ||
                            f.showCollectionsField ||
                            f.showNotesField) && (
                            <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2">
                              {/* -------- VARIANT -------- */}
                              {f.showVariantField && (
                                <div className="relative min-w-0">
                                  <div className="mb-1 flex items-center justify-between gap-3">
                                    <div className="text-xs font-semibold text-slate-600">
                                      Variant
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() =>
                                        updateEditForm(idx, {
                                          showVariantField: false,
                                        })
                                      }
                                      className="text-xs text-slate-400 transition hover:text-red-500"
                                      title="Remove Variant field"
                                    >
                                      ✕
                                    </button>
                                  </div>

                                  <input
                                    type="text"
                                    placeholder="e.g., bicolor"
                                    value={f.variant || ""}
                                    onChange={(e) =>
                                      updateEditForm(idx, {
                                        variant: e.target.value,
                                      })
                                    }
                                    className="block h-[48px] w-full min-w-0 truncate rounded-xl border border-[#93ca87]/40 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                    disabled={editSaving}
                                  />
                                </div>
                              )}

                              {/* -------- COLLECTIONS -------- */}
                              {f.showCollectionsField && (
                                <div className="relative min-w-0">
                                  <div className="mb-1 flex items-center justify-between gap-3">
                                    <div className="text-xs font-semibold text-slate-600">
                                      Collections
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() =>
                                        updateEditForm(idx, {
                                          showCollectionsField: false,
                                        })
                                      }
                                      className="text-xs text-slate-400 transition hover:text-red-500"
                                      title="Remove Collections field"
                                    >
                                      ✕
                                    </button>
                                  </div>

                                  <select
                                    value=""
                                    onChange={async (e) => {
                                      const value = e.target.value;

                                      if (value === "__new__") {
                                        if (!isOwnCollection) return;

                                        const name =
                                          await requestCollectionName();

                                        if (!name) return;

                                        await setDoc(
                                          doc(
                                            db,
                                            "users",
                                            dataUID,
                                            "collectionsMeta",
                                            name,
                                          ),
                                          {
                                            title: name,
                                            description: "",
                                          },
                                          { merge: true },
                                        );

                                        setCollectionsList((prev) =>
                                          [...new Set([...prev, name])].sort(),
                                        );

                                        updateEditForm(idx, {
                                          collections: [
                                            ...new Set([
                                              ...(f.collections || []),
                                              name,
                                            ]),
                                          ],
                                        });

                                        return;
                                      }

                                      if (!value) return;

                                      const exists = (
                                        f.collections || []
                                      ).includes(value);

                                      updateEditForm(idx, {
                                        collections: exists
                                          ? f.collections.filter(
                                              (collectionName) =>
                                                collectionName !== value,
                                            )
                                          : [...(f.collections || []), value],
                                      });
                                    }}
                                    className="block h-[48px] w-full min-w-0 truncate rounded-xl border border-[#93ca87]/40 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                    disabled={editSaving}
                                  >
                                    <option value="">Select collection</option>

                                    {isOwnCollection && (
                                      <option value="__new__">
                                        + Create new collection
                                      </option>
                                    )}

                                    {collectionsList.map((collectionName) => (
                                      <option
                                        key={collectionName}
                                        value={collectionName}
                                      >
                                        {collectionName}
                                      </option>
                                    ))}
                                  </select>

                                  {f.collections?.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      {f.collections.map((collectionName) => (
                                        <button
                                          key={collectionName}
                                          type="button"
                                          onClick={() =>
                                            updateEditForm(idx, {
                                              collections: f.collections.filter(
                                                (name) =>
                                                  name !== collectionName,
                                              ),
                                            })
                                          }
                                          className="max-w-full truncate rounded-lg border border-[#93ca87]/40 bg-white px-2.5 py-1 text-xs text-[#374049] transition hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                                          title={`Remove ${collectionName}`}
                                        >
                                          {collectionName} ✕
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* -------- NOTES -------- */}
                              {f.showNotesField && (
                                <div className="relative min-w-0 md:col-span-2">
                                  <div className="mb-1 flex items-center justify-between gap-3">
                                    <div className="text-xs font-semibold text-slate-600">
                                      Notes
                                    </div>

                                    <div className="flex items-center gap-3">
                                      <span className="text-[10px] text-slate-400">
                                        {(f.notes || "").length}/2000
                                      </span>

                                      <button
                                        type="button"
                                        onClick={() =>
                                          updateEditForm(idx, {
                                            showNotesField: false,
                                          })
                                        }
                                        className="text-xs text-slate-400 transition hover:text-red-500"
                                        title="Remove Notes field"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  </div>

                                  <textarea
                                    value={f.notes || ""}
                                    onChange={(e) =>
                                      updateEditForm(idx, {
                                        notes: e.target.value.slice(0, 2000),
                                      })
                                    }
                                    placeholder="Add field observations, behavior, habitat details, or anything else relevant to this photo."
                                    rows={4}
                                    maxLength={2000}
                                    disabled={editSaving}
                                    className="w-full min-w-0 resize-y rounded-xl border border-[#93ca87]/40 bg-white px-4 py-3 text-sm text-[#374049] focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {isAddingPhoto && (
                <div className="bioindex-species-editor bioindex-species-add-panel mx-auto mt-2 max-w-4xl space-y-6">
                  <div className="bioindex-species-panel-heading">
                    <div>
                      <p className="bioindex-species-section-kicker">
                        EXPAND YOUR COLLECTION
                      </p>
                      <h2>Add photographs</h2>
                      <p>
                        Add up to ten photographs to{" "}
                        <em>{selected.name || "this species"}</em>. Review each
                        observation before uploading.
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <div
                      {...getRootProps()}
                      className={`bioindex-species-dropzone w-full max-w-2xl border-2 border-dashed rounded-2xl p-7 sm:p-10 text-center cursor-pointer transition ${
                        isDragActive
                          ? "border-indigo-600 bg-indigo-50"
                          : "border-slate-300 hover:border-slate-400"
                      }`}
                    >
                      <input {...getInputProps()} />
                      <div className="text-sm font-semibold text-[#374049]">
                        {isDragActive ? (
                          "Drop photographs here"
                        ) : (
                          <>
                            <span className="sm:hidden">
                              Tap to choose photographs
                            </span>
                            <span className="hidden sm:inline">
                              Drop photographs here or click to browse
                            </span>
                          </>
                        )}
                      </div>
                      <p className="mt-2 text-xs text-[#75858a]">
                        Up to 10 photos per batch.
                      </p>
                    </div>
                  </div>

                  {addEntries.length > 0 && (
                    <div className="space-y-4">
                      {addEntries.map((entry, index) => (
                        <div
                          key={entry.id}
                          className="bioindex-species-form-card"
                        >
                          <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex w-full flex-col md:w-56">
                              <img
                                src={entry.previewUrl}
                                alt="New upload"
                                className="h-56 w-full object-cover rounded-xl border border-slate-200 md:h-40"
                              />
                              {entry.uploadStatus === "uploading" && (
                                <div className="text-xs text-blue-600 mt-1">
                                  Uploading...
                                </div>
                              )}

                              {entry.uploadStatus === "success" && (
                                <div className="text-xs text-green-600 mt-1">
                                  Uploaded ✓
                                </div>
                              )}

                              {entry.uploadStatus === "error" && (
                                <div className="text-xs text-red-600 mt-1">
                                  Upload failed
                                </div>
                              )}

                              <div className="mt-2 flex items-center justify-between gap-2">
                                <label className="flex items-center gap-2 text-xs text-slate-700">
                                  <input
                                    type="radio"
                                    name="addDefault"
                                    checked={!!entry.isDefault}
                                    onChange={() => setAddDefaultAt(index)}
                                    disabled={addSaving}
                                  />
                                  Main Photo
                                </label>

                                <button
                                  type="button"
                                  onClick={() => removeAddEntry(index)}
                                  disabled={addSaving}
                                  className="rounded-md bg-black/70 px-2 py-1 text-[11px] font-medium text-white transition hover:bg-black/80 disabled:opacity-50"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>

                            <div className="min-w-0 flex-1 space-y-4">
                              {/* DATE + LOCATION */}
                              <div className="grid min-w-0 grid-cols-1 items-end gap-4 md:grid-cols-[2fr_3fr]">
                                <div className="flex h-full min-w-0 flex-col">
                                  <div className="mb-1 text-xs font-semibold text-slate-600">
                                    Date
                                  </div>

                                  <div className="mt-auto grid grid-cols-[0.9fr_1.6fr_1.1fr] gap-2">
                                    <select
                                      value={entry.date?.day || "NA"}
                                      onChange={(event) =>
                                        updateAddEntry(index, {
                                          date: {
                                            ...normalizeDate(entry.date),
                                            day: event.target.value,
                                          },
                                        })
                                      }
                                      className="h-[48px] w-full rounded-xl border border-[#93ca87]/40 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                      disabled={addSaving}
                                    >
                                      {DAYS.map((day) => (
                                        <option key={day} value={day}>
                                          {day}
                                        </option>
                                      ))}
                                    </select>

                                    <select
                                      value={entry.date?.month || "NA"}
                                      onChange={(event) =>
                                        updateAddEntry(index, {
                                          date: {
                                            ...normalizeDate(entry.date),
                                            month: event.target.value,
                                          },
                                        })
                                      }
                                      className="h-[48px] w-full rounded-xl border border-[#93ca87]/40 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                      disabled={addSaving}
                                    >
                                      {MONTHS.map((month) => (
                                        <option
                                          key={month.value}
                                          value={month.value}
                                        >
                                          {month.label}
                                        </option>
                                      ))}
                                    </select>

                                    <select
                                      value={entry.date?.year || "NA"}
                                      onChange={(event) =>
                                        updateAddEntry(index, {
                                          date: {
                                            ...normalizeDate(entry.date),
                                            year: event.target.value,
                                          },
                                        })
                                      }
                                      className="h-[48px] w-full rounded-xl border border-[#93ca87]/40 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                      disabled={addSaving}
                                    >
                                      {YEARS.map((year) => (
                                        <option key={year} value={year}>
                                          {year}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>

                                <div className="flex h-full min-w-0 flex-col">
                                  <div className="mb-1 text-xs font-semibold text-slate-600">
                                    Location
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      setLocationModal({
                                        mode: "add",
                                        index,
                                      })
                                    }
                                    disabled={addSaving}
                                    title={
                                      displayLocation(entry) ||
                                      "Select location on map"
                                    }
                                    className="mt-auto block h-[48px] w-full min-w-0 overflow-hidden rounded-xl border border-[#93ca87]/40 bg-white px-4 text-left text-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25 disabled:opacity-50"
                                  >
                                    <span className="block w-full truncate">
                                      {displayLocation(entry) ||
                                        "Select location on map"}
                                    </span>
                                  </button>
                                </div>
                              </div>

                              {/* FORM + ADDITIONAL INFORMATION */}
                              <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[2fr_3fr]">
                                <LifeformPicker
                                  scientificName={selected?.name || ""}
                                  value={entry.label || ""}
                                  selectedKingdom={entry.lifeformKingdom || ""}
                                  onChange={(value) =>
                                    updateAddEntry(index, { label: value })
                                  }
                                  onKingdomChange={(value) =>
                                    updateAddEntry(index, {
                                      lifeformKingdom: value,
                                      label: "",
                                    })
                                  }
                                  disabled={addSaving}
                                />

                                <div className="relative min-w-0">
                                  <div className="mb-1 text-xs font-semibold text-slate-600">
                                    Additional Information
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateAddEntry(index, {
                                        showAdditionalInfo:
                                          !entry.showAdditionalInfo,
                                      })
                                    }
                                    className="flex h-[48px] w-full min-w-0 items-center justify-between gap-3 rounded-xl border border-[#93ca87]/40 bg-white px-4 text-left text-sm text-[#374049] transition hover:bg-[#24c3be]/5 focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                    disabled={addSaving}
                                  >
                                    <span className="truncate">
                                      {entry.showVariantField ||
                                      entry.showCollectionsField ||
                                      entry.showNotesField
                                        ? "Manage optional fields"
                                        : "Choose optional fields"}
                                    </span>

                                    <span
                                      className={`shrink-0 text-lg text-[#24c3be] transition-transform ${
                                        entry.showAdditionalInfo
                                          ? "rotate-180"
                                          : ""
                                      }`}
                                    >
                                      ⌄
                                    </span>
                                  </button>

                                  {entry.showAdditionalInfo && (
                                    <div className="absolute left-0 top-full z-30 mt-2 w-full rounded-2xl border border-[#93ca87]/40 bg-white p-3 shadow-lg">
                                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            updateAddEntry(index, {
                                              showVariantField:
                                                !entry.showVariantField,
                                            })
                                          }
                                          className={`rounded-xl border px-3 py-2 text-xs font-medium transition ${
                                            entry.showVariantField
                                              ? "border-[#24c3be] bg-[#24c3be] text-white"
                                              : "border-[#93ca87]/40 bg-white text-[#374049] hover:bg-[#24c3be]/10"
                                          }`}
                                        >
                                          {entry.showVariantField
                                            ? "✓ Variant"
                                            : "+ Variant"}
                                        </button>

                                        <button
                                          type="button"
                                          onClick={() =>
                                            updateAddEntry(index, {
                                              showCollectionsField:
                                                !entry.showCollectionsField,
                                            })
                                          }
                                          className={`rounded-xl border px-3 py-2 text-xs font-medium transition ${
                                            entry.showCollectionsField
                                              ? "border-[#24c3be] bg-[#24c3be] text-white"
                                              : "border-[#93ca87]/40 bg-white text-[#374049] hover:bg-[#24c3be]/10"
                                          }`}
                                        >
                                          {entry.showCollectionsField
                                            ? "✓ Collections"
                                            : "+ Collections"}
                                        </button>

                                        <button
                                          type="button"
                                          onClick={() =>
                                            updateAddEntry(index, {
                                              showNotesField:
                                                !entry.showNotesField,
                                            })
                                          }
                                          className={`rounded-xl border px-3 py-2 text-xs font-medium transition ${
                                            entry.showNotesField
                                              ? "border-[#24c3be] bg-[#24c3be] text-white"
                                              : "border-[#93ca87]/40 bg-white text-[#374049] hover:bg-[#24c3be]/10"
                                          }`}
                                        >
                                          {entry.showNotesField
                                            ? "✓ Notes"
                                            : "+ Notes"}
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* OPTIONAL FIELDS */}
                              {(entry.showVariantField ||
                                entry.showCollectionsField ||
                                entry.showNotesField) && (
                                <div className="grid min-w-0 grid-cols-1 gap-4 rounded-2xl border border-[#93CA87]/35 bg-[#FCFFF6] p-4 md:grid-cols-2">
                                  {entry.showVariantField && (
                                    <div className="relative min-w-0">
                                      <div className="mb-1 flex items-center justify-between gap-3">
                                        <div className="text-xs font-semibold text-slate-600">
                                          Variant
                                        </div>

                                        <button
                                          type="button"
                                          onClick={() =>
                                            updateAddEntry(index, {
                                              showVariantField: false,
                                            })
                                          }
                                          className="text-xs text-slate-400 transition hover:text-red-500"
                                          title="Remove Variant field"
                                        >
                                          ✕
                                        </button>
                                      </div>

                                      <input
                                        type="text"
                                        placeholder="e.g., bicolor"
                                        value={entry.variant || ""}
                                        onChange={(event) =>
                                          updateAddEntry(index, {
                                            variant: event.target.value,
                                          })
                                        }
                                        className="block h-[48px] w-full min-w-0 truncate rounded-xl border border-[#93ca87]/40 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                        disabled={addSaving}
                                      />
                                    </div>
                                  )}

                                  {entry.showCollectionsField && (
                                    <div className="relative min-w-0">
                                      <div className="mb-1 flex items-center justify-between gap-3">
                                        <div className="text-xs font-semibold text-slate-600">
                                          Collections
                                        </div>

                                        <button
                                          type="button"
                                          onClick={() =>
                                            updateAddEntry(index, {
                                              showCollectionsField: false,
                                            })
                                          }
                                          className="text-xs text-slate-400 transition hover:text-red-500"
                                          title="Remove Collections field"
                                        >
                                          ✕
                                        </button>
                                      </div>

                                      <select
                                        value=""
                                        onChange={async (event) => {
                                          const value = event.target.value;

                                          if (value === "__new__") {
                                            const name =
                                              await requestCollectionName();

                                            if (!name) return;

                                            await setDoc(
                                              doc(
                                                db,
                                                "users",
                                                dataUID,
                                                "collectionsMeta",
                                                name,
                                              ),
                                              {
                                                title: name,
                                                description: "",
                                              },
                                              { merge: true },
                                            );

                                            setCollectionsList((previous) =>
                                              [
                                                ...new Set([...previous, name]),
                                              ].sort(),
                                            );

                                            updateAddEntry(index, {
                                              collections: [
                                                ...new Set([
                                                  ...(entry.collections || []),
                                                  name,
                                                ]),
                                              ],
                                            });

                                            return;
                                          }

                                          if (!value) return;

                                          const alreadySelected = (
                                            entry.collections || []
                                          ).includes(value);

                                          updateAddEntry(index, {
                                            collections: alreadySelected
                                              ? entry.collections.filter(
                                                  (collectionName) =>
                                                    collectionName !== value,
                                                )
                                              : [
                                                  ...(entry.collections || []),
                                                  value,
                                                ],
                                          });
                                        }}
                                        className="block h-[48px] w-full min-w-0 truncate rounded-xl border border-[#93ca87]/40 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                        disabled={addSaving}
                                      >
                                        <option value="">
                                          Select collection
                                        </option>
                                        <option value="__new__">
                                          + Create new collection
                                        </option>

                                        {collectionsList.map(
                                          (collectionName) => (
                                            <option
                                              key={collectionName}
                                              value={collectionName}
                                            >
                                              {collectionName}
                                            </option>
                                          ),
                                        )}
                                      </select>

                                      {entry.collections?.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                          {entry.collections.map(
                                            (collectionName) => (
                                              <button
                                                key={collectionName}
                                                type="button"
                                                onClick={() =>
                                                  updateAddEntry(index, {
                                                    collections:
                                                      entry.collections.filter(
                                                        (name) =>
                                                          name !==
                                                          collectionName,
                                                      ),
                                                  })
                                                }
                                                className="max-w-full truncate rounded-lg border border-[#93ca87]/40 bg-white px-2.5 py-1 text-xs text-[#374049] transition hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                                                title={`Remove ${collectionName}`}
                                              >
                                                {collectionName} ✕
                                              </button>
                                            ),
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {entry.showNotesField && (
                                    <div className="relative min-w-0 md:col-span-2">
                                      <div className="mb-1 flex items-center justify-between gap-3">
                                        <div className="text-xs font-semibold text-slate-600">
                                          Notes
                                        </div>

                                        <div className="flex items-center gap-3">
                                          <span className="text-[10px] text-slate-400">
                                            {(entry.notes || "").length}/2000
                                          </span>

                                          <button
                                            type="button"
                                            onClick={() =>
                                              updateAddEntry(index, {
                                                showNotesField: false,
                                              })
                                            }
                                            className="text-xs text-slate-400 transition hover:text-red-500"
                                            title="Remove Notes field"
                                          >
                                            ✕
                                          </button>
                                        </div>
                                      </div>

                                      <textarea
                                        value={entry.notes || ""}
                                        onChange={(event) =>
                                          updateAddEntry(index, {
                                            notes: event.target.value.slice(
                                              0,
                                              2000,
                                            ),
                                          })
                                        }
                                        placeholder="Add field observations, behavior, habitat details, or anything else relevant to this photo."
                                        rows={4}
                                        maxLength={2000}
                                        disabled={addSaving}
                                        className="w-full min-w-0 resize-y rounded-xl border border-[#93ca87]/40 bg-white px-4 py-3 text-sm text-[#374049] focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                                      />
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                      <div className="text-xs text-[#63727b] text-center">
                        These photographs will be added to this species after
                        uploading.
                      </div>
                    </div>
                  )}
                  <div className="bioindex-species-panel-actions bioindex-species-add-footer">
                    <button
                      type="button"
                      onClick={handleAddPhotos}
                      disabled={addSaving || addEntries.length === 0}
                      className="bioindex-species-btn-primary"
                    >
                      {addSaving
                        ? "Uploading photos..."
                        : `Upload ${addEntries.length || ""} ${addEntries.length === 1 ? "photo" : "photos"}`}
                    </button>
                    <button
                      type="button"
                      onClick={leaveEditor}
                      disabled={addSaving}
                      className="bioindex-species-btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {confirmationDialog && (
        <div
          className="bioindex-species-dialog-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              confirmationDialog.resolve(false);
              setConfirmationDialog(null);
            }
          }}
        >
          <div
            className="bioindex-species-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="bioindex-species-confirm-title"
            aria-describedby="bioindex-species-confirm-description"
          >
            <div className="bioindex-species-dialog-topline">
              BIOINDEX · CONFIRM ACTION
            </div>
            <h2 id="bioindex-species-confirm-title">
              {confirmationDialog.title}
            </h2>
            <p id="bioindex-species-confirm-description">
              {confirmationDialog.description}
            </p>
            <div className="bioindex-species-panel-actions bioindex-species-dialog-actions">
              <button
                type="button"
                className="bioindex-species-btn-secondary"
                onClick={() => {
                  confirmationDialog.resolve(false);
                  setConfirmationDialog(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={
                  confirmationDialog.danger
                    ? "bioindex-species-btn-danger bioindex-species-dialog-danger"
                    : "bioindex-species-btn-primary"
                }
                onClick={() => {
                  confirmationDialog.resolve(true);
                  setConfirmationDialog(null);
                }}
              >
                {confirmationDialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
      {collectionNameDialog && (
        <div
          className="bioindex-species-dialog-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              collectionNameDialog.resolve(null);
              setCollectionNameDialog(null);
            }
          }}
        >
          <form
            className="bioindex-species-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bioindex-species-collection-title"
            onSubmit={(event) => {
              event.preventDefault();
              const name = collectionNameDialog.value.trim();
              if (!name) return;
              collectionNameDialog.resolve(name);
              setCollectionNameDialog(null);
            }}
          >
            <div className="bioindex-species-dialog-topline">
              YOUR COLLECTIONS
            </div>
            <h2 id="bioindex-species-collection-title">Create collection</h2>
            <label
              className="bioindex-species-field-label"
              htmlFor="bioindex-species-new-collection"
            >
              Collection name
            </label>
            <input
              id="bioindex-species-new-collection"
              autoFocus
              maxLength={100}
              value={collectionNameDialog.value}
              onChange={(event) =>
                setCollectionNameDialog((current) =>
                  current ? { ...current, value: event.target.value } : current,
                )
              }
              placeholder="Enter a collection name"
            />
            <div className="bioindex-species-panel-actions bioindex-species-dialog-actions">
              <button
                type="button"
                className="bioindex-species-btn-secondary"
                onClick={() => {
                  collectionNameDialog.resolve(null);
                  setCollectionNameDialog(null);
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bioindex-species-btn-primary"
                disabled={!collectionNameDialog.value.trim()}
              >
                Create collection
              </button>
            </div>
          </form>
        </div>
      )}

      {shareCardOpen && currentForm && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 px-3 py-4"
          onClick={() => setShareCardOpen(false)}
        >
          <div
            className="flex max-h-[96vh] w-full max-w-md flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex w-full max-w-[430px] justify-end">
              <button
                type="button"
                onClick={() => setShareCardOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-sm font-semibold text-[#374049] shadow-lg transition hover:bg-white"
                aria-label="Close share card"
              >
                ✕
              </button>
            </div>

            <div
              className="aspect-[4/5] w-full max-w-[430px] overflow-hidden bg-[#FCFFF6] shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* PHOTO */}
              <div className="relative h-[62%] overflow-hidden bg-[#374049]">
                <img
                  src={currentPhotoSrc}
                  alt={selected?.name || "Species"}
                  className="h-full w-full object-cover"
                />
              </div>

              {/* OBSERVATION INFORMATION */}
              <div className="flex h-[38%] flex-col px-5 pb-4 pt-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <h3
                      className="truncate text-[27px] italic leading-none text-[#374049]"
                      style={{ fontFamily: "Cormorant Garamond" }}
                    >
                      {selected?.name || "—"}
                    </h3>

                    {selected?.common && (
                      <p className="mt-1 truncate text-[12px] leading-tight text-slate-500">
                        {selected.common}
                      </p>
                    )}
                  </div>

                  <img
                    src="/BioIndex Prototype Logo Text.png"
                    alt="BioIndex"
                    className="h-12 w-auto max-w-[150px] shrink-0 object-contain object-right"
                  />
                </div>

                <div className="mt-2.5 flex">
                  <div className="h-[3px] w-12 bg-[#24C3BE]" />
                  <div className="h-[3px] w-6 bg-[#93CA87]" />
                </div>

                {displayLocation(currentForm) &&
                  displayLocation(currentForm) !== "Unknown" && (
                    <div className="mt-2.5">
                      <div className="text-[8px] font-bold uppercase tracking-[0.14em] text-[#24C3BE]">
                        Location
                      </div>

                      <div
                        className="text-[11px] leading-snug text-[#374049]"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {displayLocation(currentForm)}
                      </div>
                    </div>
                  )}

                <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-2">
                  {formatDateForDisplay(currentForm?.date) !== "Unknown" && (
                    <div className="min-w-[105px] flex-1">
                      <div className="text-[8px] font-bold uppercase tracking-[0.12em] text-[#24C3BE]">
                        Date
                      </div>

                      <div className="truncate text-[11px] leading-tight text-[#374049]">
                        {formatDateForDisplay(currentForm?.date)}
                      </div>
                    </div>
                  )}

                  <div className="min-w-[145px] flex-[1.35]">
                    <div className="text-[8px] font-bold uppercase tracking-[0.12em] text-[#24C3BE]">
                      Owner
                    </div>

                    <div className="truncate text-[11px] leading-tight text-[#374049]">
                      {currentForm?.ownerName ||
                        friendProfiles[dataUID]?.name ||
                        friendProfiles[currentUID]?.name ||
                        "You"}
                    </div>
                  </div>

                  {currentForm?.variant && currentForm.variant !== "NA" && (
                    <div
                      className={`min-w-[95px] flex-1 ${
                        String(currentForm.variant).length > 14
                          ? "basis-full"
                          : ""
                      }`}
                    >
                      <div className="text-[8px] font-bold uppercase tracking-[0.12em] text-[#24C3BE]">
                        Variant
                      </div>

                      <div className="truncate text-[11px] leading-tight text-[#374049]">
                        {currentForm.variant}
                      </div>
                    </div>
                  )}

                  {Array.isArray(currentForm?.collections) &&
                    currentForm.collections.length > 0 && (
                      <div className="min-w-0">
                        <div className="text-[8px] font-bold uppercase tracking-[0.12em] text-[#24C3BE]">
                          Collections
                        </div>

                        <div className="truncate text-[11px] leading-tight text-[#374049]">
                          {currentForm.collections.join(", ")}
                        </div>
                      </div>
                    )}
                </div>

                {currentForm?.notes?.trim() && (
                  <div className="mt-2 border-t border-[#93CA87]/30 pt-1.5">
                    <div className="text-[8px] font-bold uppercase tracking-[0.12em] text-[#24C3BE]">
                      Notes
                    </div>

                    <div
                      className="text-[10px] leading-snug text-slate-600"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {currentForm.notes}
                    </div>
                  </div>
                )}
                <div className="mt-auto pt-2">
                  <div className="flex justify-end border-t border-[#93CA87]/30 pt-2">
                    <span className="text-[9px] tracking-wide text-slate-400">
                      bioindex.app
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-3 text-center">
              <p className="text-[11px] text-white/65">
                Screenshot this card to share your observation.
              </p>
            </div>
          </div>
        </div>
      )}

      {adminDeleteTarget && canDeleteCollectionObservation && (
        <div
          className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 px-3 py-6"
          onClick={closeAdminDeleteObservation}
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-3xl border border-red-200 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-red-100 bg-red-50 px-6 py-5 text-center">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-red-600">
                Administrator Moderation
              </div>

              <h3
                className="mt-1 text-3xl text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                Delete Observation
              </h3>
            </div>

            <div className="p-6">
              <div className="rounded-2xl border border-[#93CA87]/35 bg-[#FCFFF6] px-4 py-4 text-center">
                <p className="text-sm font-semibold text-[#374049]">
                  <span className="italic">
                    {adminDeleteTarget.scientificName}
                  </span>

                  {adminDeleteTarget.commonName && (
                    <span className="font-normal">
                      {" "}
                      ({adminDeleteTarget.commonName})
                    </span>
                  )}
                </p>

                <p className="mt-1 text-xs text-slate-500">
                  Lifeform: {adminDeleteTarget.formLabel}
                </p>

                {adminDeleteTarget.photoCount === 1 && (
                  <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
                    This is the species record’s only photo. The empty species
                    record will also be deleted.
                  </p>
                )}
              </div>

              <p className="mt-5 text-sm leading-relaxed text-slate-600">
                This permanently removes the observation from the user’s
                archive, collections, favorites, and Firebase Storage.
              </p>

              <label className="mt-5 block">
                <span className="text-xs font-semibold uppercase tracking-wide text-[#374049]">
                  Moderation reason
                </span>

                <textarea
                  value={adminDeleteReason}
                  onChange={(event) => {
                    setAdminDeleteReason(event.target.value);
                    setAdminDeleteError("");
                  }}
                  disabled={adminDeleteSaving}
                  maxLength={500}
                  rows={4}
                  placeholder="Explain why this observation is being removed..."
                  className="mt-2 w-full resize-none rounded-2xl border border-[#93CA87]/45 bg-white px-4 py-3 text-sm text-[#374049] outline-none transition focus:border-[#24C3BE] focus:ring-2 focus:ring-[#24C3BE]/15 disabled:bg-slate-50"
                />
              </label>

              <div className="mt-1 text-right text-[10px] text-slate-400">
                {adminDeleteReason.length}/500
              </div>

              {adminDeleteError && (
                <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-600">
                  {adminDeleteError}
                </div>
              )}

              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeAdminDeleteObservation}
                  disabled={adminDeleteSaving}
                  className="rounded-xl border border-[#93CA87]/45 bg-white px-5 py-2.5 text-sm font-semibold text-[#374049] transition hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={confirmAdminDeleteObservation}
                  disabled={
                    adminDeleteSaving || adminDeleteReason.trim().length < 10
                  }
                  className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
                >
                  {adminDeleteSaving
                    ? "Deleting Observation..."
                    : "Permanently Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {renameGeminiDialog && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#374049]/70 p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget)
              setRenameGeminiDialog(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="rename-gemini-title"
            className="w-full max-w-sm rounded-3xl border border-[#93CA87]/50 bg-[#FCFFF6] p-5 shadow-2xl sm:p-6"
          >
            <div className="flex items-center gap-2">
              <img src={geminiLogo} alt="" className="h-6 w-6 object-contain" />
              <h2
                id="rename-gemini-title"
                className="text-2xl font-semibold text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                {renameGeminiDialog.accepted
                  ? "Gemini suggests"
                  : "Identification unavailable"}
              </h2>
            </div>
            {renameGeminiDialog.accepted ? (
              <p className="mt-3 text-sm leading-6 text-[#374049]">
                Gemini thinks this is{" "}
                <strong className="italic">{renameGeminiDialog.name}</strong>
                {renameGeminiDialog.commonName
                  ? ` (${renameGeminiDialog.commonName})`
                  : ""}
                .
                {renameGeminiDialog.rank === "genus"
                  ? " This is a genus-level ID. A species name is needed to save a rename here."
                  : " Review the identification before using it."}
              </p>
            ) : (
              <p className="mt-3 text-sm leading-6 text-[#374049]">
                {renameGeminiDialog.name ? (
                  <>
                    Gemini suggested{" "}
                    <strong className="italic">
                      {renameGeminiDialog.name}
                    </strong>{" "}
                    ({renameGeminiDialog.rank}). Only genus or species IDs can
                    be inserted.
                  </>
                ) : (
                  "Gemini could not suggest a genus or species for this photo. Try another photo or type a name yourself."
                )}
              </p>
            )}
            {renameGeminiDialog.name && (
              <a
                href={`https://www.inaturalist.org/search?q=${encodeURIComponent(renameGeminiDialog.name)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-[#01767A] underline decoration-[#24C3BE] underline-offset-4"
              >
                <img src={inatLogo} alt="" className="h-4 w-4 object-contain" />{" "}
                Check on iNaturalist ↗
              </a>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRenameGeminiDialog(null)}
                className="rounded-xl border border-[#93CA87]/50 px-4 py-2 text-sm font-semibold text-[#374049]"
              >
                {renameGeminiDialog.accepted ? "Cancel" : "Close"}
              </button>
              {renameGeminiDialog.accepted && (
                <button
                  type="button"
                  onClick={acceptRenameGeminiSuggestion}
                  className="rounded-xl bg-[#01767A] px-5 py-2 text-sm font-semibold text-white hover:bg-[#005f63]"
                >
                  Accept ID
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {locationModal && (
        <LocationPickerModal
          initialLocation={
            locationModal.mode === "edit"
              ? formToLocationData(editForms[locationModal.index])
              : formToLocationData(addEntries[locationModal.index])
          }
          onClose={() => setLocationModal(null)}
          onConfirm={(locationData) => {
            if (!locationData) return;

            const patch = {
              location: locationData.name,
              locationName: locationData.name,
              locationLat: locationData.lat,
              locationLng: locationData.lng,
              locationCountry: locationData.country || "",
              locationAdminArea: locationData.adminArea || "",
              locationCity: locationData.city || "",
              locationPlaceId: locationData.placeId || "",
            };

            if (locationModal.mode === "edit") {
              updateEditForm(locationModal.index, patch);
            }

            if (locationModal.mode === "add") {
              updateAddEntry(locationModal.index, patch);
            }

            setLocationModal(null);
          }}
        />
      )}
    </>
  );
}
